import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { toast } from 'sonner';
import axios from 'axios';
import {
    DollarSign, TrendingUp, TrendingDown, Upload, Link2, FileDown,
    Search, Plus, Trash2, X, Building2, RefreshCw,
    AlertTriangle, Edit, ChevronLeft, ChevronRight, ArrowDownLeft, ArrowUpRight, Eye,
    Sparkles, CheckCircle2, Layers, Calendar, GitMerge,
} from 'lucide-react';
import { OcrUploadModal } from './accounting/OcrUploadModal';
import SystemHealthBanner from './accounting/SystemHealthBanner';
import PendingImportsBar from './accounting/PendingImportsBar';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtMoney = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TABS = [
    { k: 'dashboard', l: 'Dashboard' },
    { k: 'companies', l: 'Firmalar' },
    { k: 'invoices', l: 'Faturalar' },
    { k: 'bank', l: 'Banka' },
    { k: 'reconciliation', l: 'Mutabakat' },
];

export default function Accounting() {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const initialTab = params.get('tab') || 'dashboard';
    const [tab, setTab] = useState(initialTab);
    useEffect(() => {
        const p = new URLSearchParams(location.search);
        if (p.get('tab') !== tab) {
            p.set('tab', tab);
            navigate(`${location.pathname}?${p}`, { replace: true });
        }
    }, [tab, location.pathname, location.search, navigate]);
    return (
        <DashboardLayout>
            <div className="space-y-4" data-testid="accounting-page">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-[#111827]">Muhasebe & Mutabakat</h1>
                    <div className="flex gap-2">
                        <button
                            onClick={async () => {
                                try {
                                    const r = await axios.get(`${API}/api/accounting/reports/global-ar.pdf`,
                                        { headers: headers(), responseType: 'blob' });
                                    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                } catch (e) {
                                    toast.error('PDF olusturulamadi: ' + (e.response?.data?.detail || e.message));
                                }
                            }}
                            className="h-9 px-3 bg-white hover:bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-xl flex items-center gap-1.5"
                            data-testid="acc-ar-pdf-btn"
                            title="Accounts Receivable report PDF (English) – who owes us money"
                        >
                            <FileDown size={13} /> AR Raporu (Alacaklar)
                        </button>
                        <button
                            onClick={async () => {
                                try {
                                    const r = await axios.get(`${API}/api/accounting/reports/global-ap.pdf`,
                                        { headers: headers(), responseType: 'blob' });
                                    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                } catch (e) {
                                    toast.error('PDF olusturulamadi: ' + (e.response?.data?.detail || e.message));
                                }
                            }}
                            className="h-9 px-3 bg-white hover:bg-red-50 border border-red-200 text-red-600 text-xs font-semibold rounded-xl flex items-center gap-1.5"
                            data-testid="acc-ap-pdf-btn"
                            title="Accounts Payable report PDF (English) – whom we owe money"
                        >
                            <FileDown size={13} /> AP Raporu (Borclar)
                        </button>
                    </div>
                </div>
                <div className="flex gap-1 border-b border-[#E5E7EB] pb-px overflow-x-auto">
                    {TABS.map(t => (
                        <button key={t.k} onClick={() => setTab(t.k)}
                            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.k ? 'border-[#111827] text-[#111827]' : 'border-transparent text-[#9CA3AF] hover:text-[#6B7280]'}`}
                            data-testid={`acc-tab-${t.k}`}>{t.l}</button>
                    ))}
                </div>
                <PendingImportsBar />
                <SystemHealthBanner />
                {tab === 'dashboard' && <AccDashboard />}
                {tab === 'companies' && <AccCompanies />}
                {tab === 'invoices' && <AccInvoices />}
                {tab === 'bank' && <AccBank />}
                {tab === 'reconciliation' && <AccReconciliation />}
            </div>
        </DashboardLayout>
    );
}

// ─── PAGINATION (with page numbers) ───
function Pagination({ page, total, limit, onPageChange, testId = 'pagination' }) {
    const totalPages = Math.max(1, Math.ceil((total || 0) / (limit || 50)));
    if (total === 0) return null;

    // Sayfa numaralari: 1, 2, 3, ... currentPage-1, currentPage, currentPage+1, ..., last
    const pages = [];
    const push = (n) => { if (!pages.includes(n) && n >= 1 && n <= totalPages) pages.push(n); };
    push(1); push(2);
    push(page - 1); push(page); push(page + 1);
    push(totalPages - 1); push(totalPages);
    pages.sort((a, b) => a - b);

    const display = [];
    let prev = 0;
    for (const n of pages) {
        if (prev && n - prev > 1) display.push(`gap-${prev}`);
        display.push(n);
        prev = n;
    }

    return (
        <div className="flex items-center justify-between flex-wrap gap-2 px-3 py-2 border-t border-[#F3F4F6] bg-[#FAFAFA] text-xs text-[#6B7280]" data-testid={testId}>
            <span>Toplam <strong className="text-[#111]">{total}</strong> kayit · Sayfa <strong>{page}</strong>/{totalPages}</span>
            <div className="flex items-center gap-1">
                <button onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page <= 1}
                    className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white border border-[#E5E7EB] disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`${testId}-prev`} title="Onceki">
                    <ChevronLeft size={14} />
                </button>
                {display.map((d, i) => typeof d === 'number' ? (
                    <button key={d} onClick={() => onPageChange(d)}
                        className={`min-w-[28px] h-7 px-2 rounded-lg text-[11px] font-semibold ${d === page ? 'bg-[#111827] text-white' : 'bg-white text-[#6B7280] border border-[#E5E7EB] hover:bg-[#F3F4F6]'}`}
                        data-testid={`${testId}-page-${d}`}>
                        {d}
                    </button>
                ) : (
                    <span key={d} className="px-1 text-[#9CA3AF]">…</span>
                ))}
                <button onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
                    className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white border border-[#E5E7EB] disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`${testId}-next`} title="Sonraki">
                    <ChevronRight size={14} />
                </button>
            </div>
        </div>
    );
}

// ─── DASHBOARD ───
function AccDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        axios.get(`${API}/api/accounting/dashboard`, { headers: headers() })
            .then(r => setData(r.data)).catch(() => toast.error('Dashboard yuklenemedi'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-[#F3F4F6] rounded-2xl animate-pulse" />)}</div>;
    if (!data) return null;

    return (
        <div className="space-y-4" data-testid="acc-dashboard">
            <div className="flex flex-wrap gap-2 justify-end">
                <button onClick={() => navigate('/accounting/aging')}
                    className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl flex items-center gap-1.5"
                    data-testid="acc-dashboard-aging-link">
                    <AlertTriangle size={13} />Yaslandirma Raporu
                </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard icon={TrendingUp} label="Toplam Gelir" value={`${fmtMoney(data.total_income)} EUR`} color="text-emerald-600" bg="bg-emerald-50" sub="Kesilen tum satis faturalari" />
                <KpiCard icon={TrendingDown} label="Toplam Gider" value={`${fmtMoney(data.total_expense)} EUR`} color="text-red-500" bg="bg-red-50" sub="Tum alis faturalari" />
                <KpiCard icon={ArrowDownLeft} label="Banka Giris" value={`${fmtMoney(data.bank_inflow)} EUR`} color="text-emerald-600" bg="bg-emerald-50/50" sub="Bankaya gelen toplam" />
                <KpiCard icon={ArrowUpRight} label="Banka Cikis" value={`${fmtMoney(data.bank_outflow)} EUR`} color="text-red-500" bg="bg-red-50/50" sub="Bankadan giden toplam" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard icon={DollarSign} label="Toplam Alacak" value={`${fmtMoney(data.total_receivable)} EUR`} color="text-emerald-600" bg="bg-emerald-50" sub="Acik satis faturalari" />
                <KpiCard icon={DollarSign} label="Toplam Borc" value={`${fmtMoney(data.total_payable)} EUR`} color="text-red-500" bg="bg-red-50" sub="Acik alis faturalari" />
                <KpiCard icon={AlertTriangle} label="Vadesi Gecmis" value={`${fmtMoney(data.overdue_receivable)} EUR`} color="text-amber-600" bg="bg-amber-50" sub={`${data.overdue_invoices} fatura`} />
                <KpiCard icon={DollarSign} label="Net Pozisyon" value={`${fmtMoney(data.net_position)} EUR`} color={data.net_position >= 0 ? "text-emerald-600" : "text-red-500"} bg="bg-blue-50" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                <MiniKpi label="Acik Fatura" value={data.open_invoices} />
                <MiniKpi label="Kismi Odenmis" value={data.partial_invoices} />
                <MiniKpi label="Odenmis" value={data.paid_invoices} />
                <MiniKpi label="Eslesmeyen Islem" value={data.unmatched_transactions} />
                <MiniKpi label="Eslesmis Islem" value={data.matched_transactions} />
            </div>
            {data.top_debtors.length > 0 && (
                <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                    <h3 className="text-sm font-semibold text-[#111] mb-3">En Buyuk Borclar</h3>
                    <div className="space-y-2">
                        {data.top_debtors.map((d, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5">
                                <span className="text-sm text-[#111]">{d.name}</span>
                                <span className="text-sm font-bold text-red-600">{fmtMoney(d.amount)} EUR</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {data.recent_transactions.length > 0 && (
                <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                    <h3 className="text-sm font-semibold text-[#111] mb-3">Son Banka Islemleri</h3>
                    <div className="space-y-1.5">
                        {data.recent_transactions.slice(0, 5).map((t, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#F3F4F6] last:border-0">
                                <div>
                                    <span className="text-sm text-[#111]">{t.counterparty_name || 'Bilinmeyen'}</span>
                                    <span className="text-[11px] text-[#9CA3AF] ml-2">{t.transaction_date}</span>
                                </div>
                                <span className={`text-sm font-bold ${t.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{t.amount >= 0 ? '+' : ''}{fmtMoney(t.amount)} EUR</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── COMPANIES (Musteri / Tedarikci / Bankadan Gelenler sub-tabs) ───
function AccCompanies() {
    const navigate = useNavigate();
    const [subTab, setSubTab] = useState('customer');   // 'customer' | 'supplier' | 'bank'
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [detailId, setDetailId] = useState(null);
    const [bankDetailId, setBankDetailId] = useState(null);   // Bankadan gelen firma detayi (transferler + merge)
    // Coklu secim - birlestirme icin
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showMergeModal, setShowMergeModal] = useState(false);

    const fetch = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (subTab === 'bank') {
            params.set('source', 'bank');
        } else if (subTab === 'all') {
            // Tumu: hicbir role filter koyma
        } else {
            params.set('role', subTab);
        }
        if (search) params.set('q', search);
        params.set('page', String(page));
        params.set('limit', String(limit));
        axios.get(`${API}/api/accounting/companies?${params}`, { headers: headers() })
            .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
            .catch(() => toast.error('Yuklenemedi'))
            .finally(() => setLoading(false));
    }, [search, subTab, page, limit]);

    useEffect(() => { fetch(); }, [fetch]);
    useEffect(() => { setPage(1); }, [search, subTab]);

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm('Firma silinsin mi?')) return;
        try { await axios.delete(`${API}/api/accounting/companies/${id}`, { headers: headers() }); fetch(); toast.success('Silindi'); }
        catch { toast.error('Silinemedi'); }
    };

    const [syncing, setSyncing] = useState(false);
    const handleSyncFromBank = async () => {
        if (!window.confirm('Banka hareketlerindeki tum karsi taraflari Firmalar listesine senkronize et?\n\n- Mevcut firmalarla otomatik birlestirilir (IBAN/isim)\n- Yenileri eklenir')) return;
        setSyncing(true);
        try {
            const r = await axios.post(`${API}/api/accounting/companies/sync-from-bank`, {}, { headers: headers() });
            toast.success(`Tamam: ${r.data.created} yeni firma eklendi, ${r.data.matched} mevcut firmaya baglandi (${r.data.total_unique} toplam)`, { duration: 8000 });
            fetch();
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail;
            toast.error(detail ? `${detail} (HTTP ${status || '?'})` : `Senkronizasyon hatasi (HTTP ${status || 'baglanti'})`, { duration: 8000 });
        } finally { setSyncing(false); }
    };

    const [cleaning, setCleaning] = useState(false);
    const handleCleanupEmpty = async () => {
        setCleaning(true);
        try {
            // Once dry run: kac firma silinecek?
            const dry = await axios.post(`${API}/api/accounting/companies/cleanup-empty?dry_run=true`, {}, { headers: headers() });
            const count = dry.data.to_delete || 0;
            if (count === 0) {
                toast.success('Silinecek bos firma yok - hepsi kullaniliyor', { duration: 5000 });
                setCleaning(false);
                return;
            }
            const sampleNames = (dry.data.items || []).slice(0, 5).map(i => i.name || '(isimsiz)').join(', ');
            const moreText = count > 5 ? ` ve ${count - 5} digerleri...` : '';
            const ok = window.confirm(
                `${count} firma silinecek (0 fatura, 0 transfer, 0 ekstre).\n\n` +
                `Ornek: ${sampleNames}${moreText}\n\n` +
                `Devam edilsin mi?`
            );
            if (!ok) { setCleaning(false); return; }
            const r = await axios.post(`${API}/api/accounting/companies/cleanup-empty`, {}, { headers: headers() });
            toast.success(`${r.data.deleted} bos firma silindi (${r.data.checked} kontrol edildi)`, { duration: 8000 });
            fetch();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(detail ? `${detail}` : 'Temizlik hatasi', { duration: 6000 });
        } finally { setCleaning(false); }
    };

    return (
        <div className="space-y-3" data-testid="acc-companies">
            {/* Sub-tabs Tumu / Musteri / Tedarikci / Bankadan Gelenler */}
            <div className="flex gap-1 bg-[#F3F4F6] rounded-xl p-1 w-fit">
                <button onClick={() => setSubTab('all')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${subTab === 'all' ? 'bg-white text-[#111] shadow-sm' : 'text-[#6B7280]'}`}
                    data-testid="acc-subtab-all">Tumu</button>
                <button onClick={() => setSubTab('customer')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${subTab === 'customer' ? 'bg-white text-[#111] shadow-sm' : 'text-[#6B7280]'}`}
                    data-testid="acc-subtab-customer">Musteriler</button>
                <button onClick={() => setSubTab('supplier')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${subTab === 'supplier' ? 'bg-white text-[#111] shadow-sm' : 'text-[#6B7280]'}`}
                    data-testid="acc-subtab-supplier">Tedarikciler</button>
                <button onClick={() => setSubTab('bank')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 ${subTab === 'bank' ? 'bg-white text-[#8B5CF6] shadow-sm' : 'text-[#6B7280]'}`}
                    data-testid="acc-subtab-bank" title="Bankadan otomatik eklenmis, henuz fatura kayitlari olmayan firmalar">
                    <Sparkles size={11} />Bankadan Gelenler
                </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Firma, KDV no, IBAN ara..."
                        className="w-full h-9 pl-9 pr-3 bg-white border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" data-testid="acc-company-search" />
                </div>
                <button onClick={handleSyncFromBank} disabled={syncing}
                    className="h-9 px-4 bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white text-sm font-medium rounded-xl flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="acc-sync-from-bank-btn"
                    title="Banka hareketlerindeki firmalari Firmalar tablosuna senkronize et (mevcutlarla birlestirir, yenileri ekler)">
                    <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
                    {syncing ? 'Senkron...' : 'Banka ile Senkronize Et'}
                </button>
                <button onClick={() => navigate('/accounting/duplicates')}
                    className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-xl flex items-center gap-1.5"
                    data-testid="acc-dup-link-btn"
                    title="Duplicate firma ve faturalari listele/temizle">
                    <AlertTriangle size={13} />
                    Duplicate Kontrol
                </button>
                <button onClick={handleCleanupEmpty} disabled={cleaning}
                    className="h-9 px-4 bg-red-50 border border-red-200 text-red-700 text-sm font-medium rounded-xl flex items-center gap-1.5 hover:bg-red-100 disabled:opacity-50"
                    data-testid="acc-cleanup-empty-btn"
                    title="0 fatura + 0 transfer + 0 ekstre olan bos firmalari sil">
                    <Trash2 size={13} className={cleaning ? 'animate-pulse' : ''} />
                    {cleaning ? 'Kontrol...' : 'Bos Firmalari Temizle'}
                </button>
                <button onClick={() => { setEditItem(null); setShowForm(true); }} className="h-9 px-4 bg-[#111827] text-white text-sm font-medium rounded-xl flex items-center gap-1.5" data-testid="acc-add-company-btn">
                    <Plus size={14} />Firma Ekle
                </button>
            </div>

            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                {/* SECIM BARI - 2+ firma secince ust kisimda cikar */}
                {selectedIds.size > 0 && (
                    <div className="px-4 py-2.5 bg-[#111827] text-white flex items-center justify-between gap-3 flex-wrap" data-testid="acc-comp-selection-bar">
                        <div className="text-[12px] font-semibold flex items-center gap-2">
                            <CheckCircle2 size={14} />
                            {selectedIds.size} firma secili
                            {selectedIds.size > 5 && <span className="text-[10px] text-amber-300">(en fazla 5 onerilir)</span>}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setSelectedIds(new Set())}
                                className="h-7 px-3 bg-white/10 hover:bg-white/20 text-white text-[11px] rounded-md"
                                data-testid="acc-comp-selection-clear">
                                Temizle
                            </button>
                            <button onClick={() => setShowMergeModal(true)}
                                disabled={selectedIds.size < 2}
                                className="h-7 px-3 bg-orange-500 hover:bg-orange-600 text-white text-[11px] font-bold rounded-md flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="acc-comp-selection-merge-btn"
                                title={selectedIds.size < 2 ? 'En az 2 firma sec' : 'Secili firmalari tek firmada birlestir'}>
                                <GitMerge size={12} />
                                Birlestir ({selectedIds.size})
                            </button>
                        </div>
                    </div>
                )}
                {loading ? <div className="p-8 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div> :
                    items.length === 0 ? <div className="p-8 text-center text-sm text-[#9CA3AF]">{subTab === 'all' ? 'Firma' : subTab === 'customer' ? 'Musteri' : subTab === 'supplier' ? 'Tedarikci' : 'Bankadan firma'} bulunamadi</div> : (
                        <>
                            <div className="divide-y divide-[#F3F4F6]">
                                {items.map(c => {
                                    const isSelected = selectedIds.has(c.id);
                                    return (
                                    <div key={c.id} className={`flex items-center gap-2 px-4 py-3 hover:bg-[#F9FAFB] ${isSelected ? 'bg-orange-50/60 ring-1 ring-orange-200' : ''}`} data-testid={`acc-company-row-${c.id}`}>
                                        <input type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                setSelectedIds(prev => {
                                                    const next = new Set(prev);
                                                    next.has(c.id) ? next.delete(c.id) : next.add(c.id);
                                                    return next;
                                                });
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-4 h-4 accent-orange-500 cursor-pointer shrink-0"
                                            data-testid={`acc-company-checkbox-${c.id}`}
                                            aria-label={`${c.name || 'isimsiz'} firmasini sec`}
                                        />
                                        <button onClick={() => navigate(`/accounting/companies/${c.id}`)} className="flex-1 flex items-center gap-3 text-left min-w-0" data-testid={`acc-company-${c.id}`}>
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${subTab === 'bank' ? 'bg-[#8B5CF6]/10' : 'bg-[#007AFF]/10'}`}>
                                                <Building2 size={14} className={subTab === 'bank' ? 'text-[#8B5CF6]' : 'text-[#007AFF]'} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-medium text-[#111] truncate">{c.name || '(isimsiz)'}</div>
                                                <div className="flex gap-2 mt-0.5 items-center flex-wrap">
                                                    {c.vat_number && <span className="text-[10px] text-[#9CA3AF]">{c.vat_number}</span>}
                                                    {c.iban && <span className="text-[10px] text-[#9CA3AF] truncate">{c.iban}</span>}
                                                    {subTab === 'bank' && <span className="text-[10px] text-[#8B5CF6] font-semibold">Bankadan otomatik</span>}
                                                </div>
                                            </div>
                                            <Eye size={14} className="text-[#9CA3AF]" />
                                        </button>
                                        <span onClick={(e) => { e.stopPropagation(); setEditItem(c); setShowForm(true); }} className="w-7 h-7 rounded-lg flex items-center justify-center text-[#9CA3AF] hover:text-[#007AFF] hover:bg-[#007AFF]/10 cursor-pointer"><Edit size={13} /></span>
                                        <span onClick={(e) => handleDelete(c.id, e)} className="w-7 h-7 rounded-lg flex items-center justify-center text-[#D1D5DB] hover:text-red-500 hover:bg-red-50 cursor-pointer"><Trash2 size={13} /></span>
                                    </div>
                                );})}
                            </div>
                            <Pagination page={page} total={total} limit={limit} onPageChange={setPage} testId="acc-companies-pagination" />
                        </>
                    )}
            </div>

            {showForm && <CompanyForm item={editItem} defaultRole={subTab === 'bank' ? 'customer' : subTab} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetch(); }} />}
            {detailId && <CompanyDetailModal companyId={detailId} onClose={() => setDetailId(null)} />}
            {bankDetailId && <BankCompanyDetailModal companyId={bankDetailId} onClose={() => setBankDetailId(null)} onMerged={() => { setBankDetailId(null); fetch(); }} />}
            {showMergeModal && (
                <BulkMergeModal
                    selectedIds={Array.from(selectedIds)}
                    items={items}
                    onClose={() => setShowMergeModal(false)}
                    onMerged={() => { setShowMergeModal(false); setSelectedIds(new Set()); fetch(); }}
                />
            )}
        </div>
    );
}

// ============================================================
// BulkMergeModal - listede secili N firmayi tek firmaya birlestir
// (current sayfanin items'inda olmayan, daha onceden secilmis firmalar
//  da olabilir; eksik bilgiyi backend'den cek)
// ============================================================
function BulkMergeModal({ selectedIds, items, onClose, onMerged }) {
    const [targetId, setTargetId] = useState(selectedIds[0]);
    const [merging, setMerging] = useState(false);
    const [details, setDetails] = useState({});  // id -> {name, vat_number, iban, invoices, transfers}

    // Secili firmalar hakkinda detay topla (zenginlestirilmis goruntu icin)
    useEffect(() => {
        const idsToFetch = selectedIds.filter(id => !items.find(i => i.id === id));
        // Items'da olanlardan dogrudan map kur
        const map = {};
        for (const it of items) {
            if (selectedIds.includes(it.id)) map[it.id] = it;
        }
        setDetails(map);
        // Eksik olanlari paralel cek
        if (idsToFetch.length > 0) {
            Promise.all(idsToFetch.map(id =>
                axios.get(`${API}/api/accounting/companies/${id}`, { headers: headers() })
                    .then(r => ({ id, data: r.data }))
                    .catch(() => null)
            )).then(results => {
                setDetails(prev => {
                    const next = { ...prev };
                    for (const r of results) {
                        if (r) next[r.id] = r.data;
                    }
                    return next;
                });
            });
        }
    }, [selectedIds, items]);

    const target = details[targetId];
    const sources = selectedIds.filter(id => id !== targetId);

    const handleMerge = async () => {
        if (!targetId || sources.length === 0) return;
        const tName = details[targetId]?.name || '(isimsiz)';
        const sNames = sources.map(id => details[id]?.name || '(isimsiz)').join(', ');
        const ok = window.confirm(
            `${sources.length} firma silinecek ve ${tName} altina aktarilacak.\n\n` +
            `Silinecekler:\n${sNames}\n\n` +
            `Devam edilsin mi?`
        );
        if (!ok) return;
        setMerging(true);
        let success = 0;
        let fail = 0;
        try {
            // Her source'u sirali olarak target'a merge et
            for (const srcId of sources) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await axios.post(`${API}/api/accounting/companies/merge`,
                        { source_id: srcId, target_id: targetId }, { headers: headers() });
                    success++;
                } catch (e) {
                    fail++;
                    console.error('Merge failed:', srcId, e);
                }
            }
            if (fail === 0) {
                toast.success(`${success} firma '${tName}' altinda birlesti`);
            } else {
                toast.error(`${success} basarili, ${fail} basarisiz`);
            }
            onMerged();
        } finally { setMerging(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="acc-comp-bulk-merge-modal">
            <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="px-5 py-3 border-b border-[#F3F4F6] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <GitMerge size={16} className="text-orange-600" />
                        <h2 className="text-sm font-bold text-[#111]">Firma Birlestirme ({selectedIds.length} firma)</h2>
                    </div>
                    <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111]" data-testid="acc-comp-bulk-merge-close"><X size={16} /></button>
                </div>

                <div className="p-5 space-y-3 overflow-y-auto">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-[11px] text-blue-900">
                        <strong>Hedef firma sec:</strong> Diger firmalar bu firmanin altina tasinacak (fatura/transfer/ekstre dahil),
                        digerleri silinecek. Bu islem <strong>"Duplicate Kontrol &gt; Birlesmis Firmalar"</strong>
                        sayfasindan "Ayir" ile geri alinabilir.
                    </div>

                    <div className="text-[11px] font-semibold text-[#6B7280]">Hedef (kalacak) firma:</div>
                    <div className="space-y-1.5">
                        {selectedIds.map(id => {
                            const c = details[id] || { name: 'Yukleniyor...' };
                            const isTarget = id === targetId;
                            return (
                                <label key={id}
                                    className={`flex items-start gap-2 p-3 rounded-lg cursor-pointer border-2 transition-all ${isTarget ? 'border-emerald-500 bg-emerald-50' : 'border-[#E5E7EB] hover:border-orange-300'}`}
                                    data-testid={`acc-comp-bulk-merge-pick-${id}`}>
                                    <input type="radio" name="merge-target" checked={isTarget}
                                        onChange={() => setTargetId(id)}
                                        className="mt-0.5 accent-emerald-600" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-[13px] font-bold truncate ${isTarget ? 'text-emerald-700' : 'text-[#111]'}`}>{c.name || '(isimsiz)'}</span>
                                            {isTarget && <span className="text-[9px] px-1.5 py-0.5 bg-emerald-600 text-white font-bold rounded">HEDEF</span>}
                                            {!isTarget && <span className="text-[9px] px-1.5 py-0.5 bg-red-100 text-red-700 font-bold rounded">SILINECEK</span>}
                                        </div>
                                        <div className="text-[10px] text-[#9CA3AF] mt-0.5 flex gap-2 flex-wrap">
                                            {c.vat_number && <span>KDV {c.vat_number}</span>}
                                            {c.iban && <span className="truncate">{c.iban}</span>}
                                            {c.roles && c.roles.length > 0 && <span>{c.roles.join('/')}</span>}
                                        </div>
                                    </div>
                                </label>
                            );
                        })}
                    </div>

                    {target && sources.length > 0 && (
                        <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3 text-[11px] text-[#374151]">
                            <strong className="text-emerald-700">{target.name}</strong> altina <strong>{sources.length}</strong> firma tasinacak.
                            Hedef firmanin VAT/IBAN/adres alanlari bos ise kaynaklardan tamamlanir.
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-[#F3F4F6] flex items-center justify-end gap-2 bg-[#FAFAFA]">
                    <button onClick={onClose} className="h-9 px-4 bg-white border border-[#E5E7EB] text-[#6B7280] text-xs font-semibold rounded-lg">Vazgec</button>
                    <button onClick={handleMerge} disabled={merging || !targetId || sources.length === 0}
                        className="h-9 px-4 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                        data-testid="acc-comp-bulk-merge-confirm">
                        {merging ? <RefreshCw size={12} className="animate-spin" /> : <GitMerge size={12} />}
                        {merging ? 'Birlestiriliyor...' : `${sources.length} Firma Birlestir`}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CompanyForm({ item, defaultRole, onClose, onSaved }) {
    const [form, setForm] = useState(item || { name: '', vat_number: '', iban: '', email: '', phone: '', address: '', roles: [defaultRole || 'customer'], notes: '' });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const toggleRole = (r) => {
        const roles = form.roles || [];
        set('roles', roles.includes(r) ? roles.filter(x => x !== r) : [...roles, r]);
    };
    const handleSubmit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            if (item?.id) await axios.put(`${API}/api/accounting/companies/${item.id}`, form, { headers: headers() });
            else await axios.post(`${API}/api/accounting/companies`, form, { headers: headers() });
            toast.success(item?.id ? 'Guncellendi' : 'Eklendi'); onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
            <form onSubmit={handleSubmit} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3" data-testid="acc-company-form">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-bold text-[#111]">{item?.id ? 'Firma Duzenle' : 'Yeni Firma'}</h3>
                    <button type="button" onClick={onClose} className="text-[#9CA3AF] hover:text-[#111]"><X size={18} /></button>
                </div>
                <input value={form.name || ''} onChange={e => set('name', e.target.value)} placeholder="Firma Adi *" required className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" data-testid="acc-company-name-input" />
                <div className="grid grid-cols-2 gap-2">
                    <input value={form.vat_number || ''} onChange={e => set('vat_number', e.target.value)} placeholder="KDV No" className="h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" data-testid="acc-company-vat-input" />
                    <input value={form.iban || ''} onChange={e => set('iban', e.target.value)} placeholder="IBAN" className="h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" data-testid="acc-company-iban-input" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <input value={form.email || ''} onChange={e => set('email', e.target.value)} placeholder="Email" className="h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" />
                    <input value={form.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="Telefon" className="h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" />
                </div>
                <input value={form.address || ''} onChange={e => set('address', e.target.value)} placeholder="Adres" className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" />
                <div>
                    <label className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider">Roller</label>
                    <div className="flex gap-2 mt-1">
                        {['customer', 'supplier'].map(r => (
                            <button key={r} type="button" onClick={() => toggleRole(r)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${(form.roles || []).includes(r) ? 'bg-[#111827] text-white' : 'bg-[#F3F4F6] text-[#6B7280]'}`}
                                data-testid={`acc-role-${r}`}>{r === 'customer' ? 'Musteri' : 'Tedarikci'}</button>
                        ))}
                    </div>
                </div>
                <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Notlar" rows={2} className="w-full px-3 py-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF] resize-none" />
                <button type="submit" disabled={saving} className="w-full h-10 bg-[#111827] text-white text-sm font-semibold rounded-xl disabled:opacity-50" data-testid="acc-company-save-btn">
                    {saving ? 'Kaydediliyor...' : item?.id ? 'Guncelle' : 'Firma Ekle'}
                </button>
            </form>
        </div>
    );
}

// ─── COMPANY DETAIL MODAL (Cari Ekstre) ───
function CompanyDetailModal({ companyId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('all');   // 'all' | 'sales' | 'purchase' | 'reconciliation'

    const fetchData = useCallback(() => {
        return axios.get(`${API}/api/accounting/companies/${companyId}`, { headers: headers() })
            .then(r => setData(r.data)).catch(() => toast.error('Detay yuklenemedi'))
            .finally(() => setLoading(false));
    }, [companyId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const filteredInvoices = useMemo(() => {
        if (!data) return [];
        if (view === 'sales') return data.invoices.filter(i => i.type === 'sales');
        if (view === 'purchase') return data.invoices.filter(i => i.type === 'purchase');
        return data.invoices;
    }, [data, view]);

    const isCustomer = data?.roles?.includes('customer');
    const isSupplier = data?.roles?.includes('supplier');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="acc-company-detail">
                {loading ? <div className="p-12 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div> :
                    !data ? <div className="p-12 text-center">Bulunamadi</div> : (
                        <>
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-[#F3F4F6] flex items-start justify-between gap-4 shrink-0">
                                <div className="min-w-0">
                                    <h2 className="text-lg font-bold text-[#111] truncate" data-testid="acc-detail-name">{data.name || '(isimsiz)'}</h2>
                                    <div className="flex flex-wrap gap-2 mt-1 text-[11px] text-[#6B7280]">
                                        {data.vat_number && <span>KDV: {data.vat_number}</span>}
                                        {data.iban && <span>IBAN: {data.iban}</span>}
                                        {data.email && <span>{data.email}</span>}
                                        {data.phone && <span>{data.phone}</span>}
                                    </div>
                                    <div className="flex gap-1.5 mt-2">
                                        {data.roles?.map(r => <span key={r} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${r === 'customer' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>{r === 'customer' ? 'Musteri' : 'Tedarikci'}</span>)}
                                    </div>
                                </div>
                                <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111] shrink-0"><X size={20} /></button>
                            </div>

                            {/* Summary KPIs */}
                            <div className="px-6 py-4 border-b border-[#F3F4F6] grid grid-cols-2 md:grid-cols-4 gap-3 shrink-0">
                                {isCustomer && (
                                    <>
                                        <DetailKpi label="Kestigim Faturalar" value={`${fmtMoney(data.sales_total)} EUR`} sub={`${data.invoices.filter(i => i.type === 'sales').length} fatura`} color="text-[#111]" />
                                        <DetailKpi label="Tahsil Edilen" value={`${fmtMoney(data.sales_paid)} EUR`} color="text-emerald-600" />
                                        <DetailKpi label="Bana Olan Borcu" value={`${fmtMoney(data.sales_remaining)} EUR`} color="text-red-500" testId="acc-detail-sales-remaining" />
                                    </>
                                )}
                                {isSupplier && (
                                    <>
                                        <DetailKpi label="Aldigim Faturalar" value={`${fmtMoney(data.purchase_total)} EUR`} sub={`${data.invoices.filter(i => i.type === 'purchase').length} fatura`} color="text-[#111]" />
                                        <DetailKpi label="Toplam Odedigim" value={`${fmtMoney(data.purchase_paid)} EUR`} color="text-emerald-600" />
                                        <DetailKpi label="Toplam Borcum" value={`${fmtMoney(data.purchase_remaining)} EUR`} color="text-red-500" testId="acc-detail-purchase-remaining" />
                                    </>
                                )}
                                <DetailKpi label="Net Bakiye" value={`${fmtMoney(data.net_balance)} EUR`} color={data.net_balance >= 0 ? "text-emerald-600" : "text-red-500"} sub={data.net_balance >= 0 ? '(Alacakli)' : '(Borclu)'} />
                            </div>

                            {/* Tab navigation */}
                            <div className="px-6 border-b border-[#F3F4F6] flex gap-1 shrink-0">
                                {[['all', 'Tum Faturalar'], ...(isCustomer && isSupplier ? [['sales', 'Satis'], ['purchase', 'Alis']] : []), ['reconciliation', 'Mutabakat']].map(([k, l]) => (
                                    <button key={k} onClick={() => setView(k)}
                                        className={`px-4 py-2 text-xs font-semibold border-b-2 ${view === k ? (k === 'reconciliation' ? 'border-[#8B5CF6] text-[#8B5CF6]' : 'border-[#111] text-[#111]') : 'border-transparent text-[#9CA3AF]'} ${k === 'reconciliation' ? 'flex items-center gap-1' : ''}`}
                                        data-testid={`acc-detail-tab-${k}`}>
                                        {k === 'reconciliation' && <Sparkles size={11} />}{l}
                                    </button>
                                ))}
                            </div>

                            {/* Tab content */}
                            <div className="flex-1 overflow-y-auto">
                                {view === 'reconciliation' ? (
                                    <CompanyReconciliationPanel companyId={companyId} onChange={fetchData} />
                                ) : (
                                    filteredInvoices.length === 0 ?
                                        <div className="p-12 text-center text-sm text-[#9CA3AF]">Fatura bulunamadi</div> :
                                        <table className="w-full text-sm">
                                            <thead className="bg-[#FAFAFA] sticky top-0 z-10">
                                                <tr className="text-[10px] text-[#9CA3AF] uppercase">
                                                    <th className="text-left px-4 py-2 font-semibold">Fatura</th>
                                                    <th className="text-left px-4 py-2 font-semibold">Tip</th>
                                                    <th className="text-left px-4 py-2 font-semibold">Fatura Tarihi</th>
                                                    <th className="text-left px-4 py-2 font-semibold">Vade</th>
                                                    <th className="text-right px-4 py-2 font-semibold">Tutar</th>
                                                    <th className="text-right px-4 py-2 font-semibold">Odenen</th>
                                                    <th className="text-right px-4 py-2 font-semibold">Kalan</th>
                                                    <th className="text-left px-4 py-2 font-semibold">Son Odeme</th>
                                                    <th className="text-center px-4 py-2 font-semibold">Durum</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[#F3F4F6]">
                                                {filteredInvoices.map(inv => (
                                                    <tr key={inv.id} className="hover:bg-[#F9FAFB]" data-testid={`acc-detail-inv-${inv.id}`}>
                                                        <td className="px-4 py-2 font-medium text-[#111] truncate max-w-[200px]">{inv.invoice_number}</td>
                                                        <td className="px-4 py-2"><span className={`text-[10px] font-medium ${inv.type === 'sales' ? 'text-emerald-600' : 'text-blue-600'}`}>{inv.type === 'sales' ? 'Satis' : 'Alis'}</span></td>
                                                        <td className="px-4 py-2 text-[#6B7280] text-xs">{inv.invoice_date || '-'}</td>
                                                        <td className="px-4 py-2 text-[#6B7280] text-xs">{inv.due_date || '-'}</td>
                                                        <td className="px-4 py-2 text-right font-bold text-[#111]">{fmtMoney(inv.total)}</td>
                                                        <td className="px-4 py-2 text-right text-emerald-600">{fmtMoney(inv.paid_amount)}</td>
                                                        <td className="px-4 py-2 text-right font-bold text-red-500">{fmtMoney(inv.remaining)}</td>
                                                        <td className="px-4 py-2 text-[#6B7280] text-xs">{inv.last_payment_date || '-'}</td>
                                                        <td className="px-4 py-2 text-center">
                                                            <StatusBadge status={inv.status} />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                )}
                            </div>
                        </>
                    )}
            </div>
        </div>
    );
}

function DetailKpi({ label, value, color, sub, testId }) {
    return (
        <div className="bg-[#F9FAFB] rounded-xl p-3" data-testid={testId}>
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-base font-bold ${color || 'text-[#111]'}`}>{value}</p>
            {sub && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{sub}</p>}
        </div>
    );
}

// ─── COMPANY RECONCILIATION PANEL (firma icinde manuel + AI oneri eslestirme) ───
function CompanyReconciliationPanel({ companyId, onChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedTxId, setSelectedTxId] = useState(null);
    const [selectedInvIds, setSelectedInvIds] = useState(new Set());
    const [matching, setMatching] = useState(false);
    const [processingSugId, setProcessingSugId] = useState(null);

    const fetchAll = useCallback(() => {
        setLoading(true);
        axios.get(`${API}/api/accounting/companies/${companyId}/reconciliation`, { headers: headers() })
            .then(r => setData(r.data))
            .catch(() => toast.error('Mutabakat verisi yuklenemedi'))
            .finally(() => setLoading(false));
    }, [companyId]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const toggleInv = (id) => {
        setSelectedInvIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleManualMatch = async () => {
        if (!selectedTxId || selectedInvIds.size === 0) {
            return toast.error('Bir transfer ve en az bir fatura sec');
        }
        const tx = data.unmatched_transactions.find(t => t.id === selectedTxId);
        const invs = data.open_invoices.filter(i => selectedInvIds.has(i.id));
        const txAmt = Math.abs(tx.amount);
        const sumInv = invs.reduce((s, i) => s + i.remaining, 0);
        const diff = Math.abs(txAmt - sumInv);
        if (diff > Math.max(0.5, txAmt * 0.01)) {
            if (!window.confirm(`Tutar uyusmuyor: transfer ${fmtMoney(txAmt)} EUR, faturalar ${fmtMoney(sumInv)} EUR (fark ${fmtMoney(diff)} EUR). Yine de eslestir?`)) return;
        }
        setMatching(true);
        try {
            const r = await axios.post(`${API}/api/accounting/reconciliation/match`,
                { transaction_id: selectedTxId, invoice_ids: Array.from(selectedInvIds) },
                { headers: headers() });
            toast.success(`Eslestirildi: ${r.data.distributed.length} fatura kapatildi${r.data.remaining_unallocated > 0 ? ` (${fmtMoney(r.data.remaining_unallocated)} EUR artmis)` : ''}`);
            setSelectedTxId(null); setSelectedInvIds(new Set());
            fetchAll(); onChange && onChange();
        } catch (e) { toast.error(e.response?.data?.detail || 'Eslestirme hatasi'); }
        finally { setMatching(false); }
    };

    const handleApproveSug = async (sug, invoiceId, isMulti) => {
        if (processingSugId) return;
        setProcessingSugId(sug.id);
        try {
            if (isMulti) {
                await axios.post(`${API}/api/accounting/reconciliation/suggestions/${sug.id}/approve`,
                    { multi: true }, { headers: headers() });
                toast.success(`${sug.multi_match.count} fatura onaylandi`);
            } else {
                await axios.post(`${API}/api/accounting/reconciliation/suggestions/${sug.id}/approve`,
                    invoiceId ? { invoice_id: invoiceId } : {}, { headers: headers() });
                toast.success('Onaylandi');
            }
            fetchAll(); onChange && onChange();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setProcessingSugId(null); }
    };

    const handleRejectSug = async (sug) => {
        if (processingSugId) return;
        setProcessingSugId(sug.id);
        try {
            await axios.post(`${API}/api/accounting/reconciliation/suggestions/${sug.id}/reject`, {}, { headers: headers() });
            toast.success('Reddedildi');
            fetchAll();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setProcessingSugId(null); }
    };

    if (loading) return <div className="p-12 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div>;
    if (!data) return <div className="p-12 text-center text-sm text-[#9CA3AF]">Veri yok</div>;

    const selectedTx = data.unmatched_transactions.find(t => t.id === selectedTxId);
    const txAmt = selectedTx ? Math.abs(selectedTx.amount) : 0;
    const sumSelInv = data.open_invoices.filter(i => selectedInvIds.has(i.id)).reduce((s, i) => s + i.remaining, 0);

    return (
        <div className="p-4 space-y-3" data-testid="acc-comp-recon">
            {/* Mini KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <DetailKpi label="Acik Alacak" value={`${fmtMoney(data.totals.open_receivable)} EUR`} color="text-emerald-600" sub={`${data.open_invoices.filter(i => i.type === 'sales').length} satis fatura`} />
                <DetailKpi label="Acik Borc" value={`${fmtMoney(data.totals.open_payable)} EUR`} color="text-red-500" sub={`${data.open_invoices.filter(i => i.type === 'purchase').length} alis fatura`} />
                <DetailKpi label="Eslesmemis Gelen" value={`+${fmtMoney(data.totals.unmatched_in)} EUR`} color="text-emerald-600" />
                <DetailKpi label="Eslesmemis Giden" value={`-${fmtMoney(data.totals.unmatched_out)} EUR`} color="text-red-500" />
            </div>

            {/* AI Suggestions for this company (top priority) */}
            {data.suggestions.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 space-y-2" data-testid="acc-comp-recon-suggestions">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-[#8B5CF6]">
                        <Sparkles size={12} />AI Onerileri ({data.suggestions.length})
                    </div>
                    {data.suggestions.slice(0, 5).map(sug => {
                        const aiPick = sug.candidates.find(c => c.invoice_id === sug.ai_suggested_invoice_id) || sug.candidates[0];
                        const tx = sug.transaction_snapshot;
                        const isProc = processingSugId === sug.id;
                        if (!aiPick) return null;
                        return (
                            <div key={sug.id} className="bg-white rounded-lg p-2.5 flex items-center gap-2 flex-wrap text-[12px]" data-testid={`acc-comp-sug-${sug.id}`}>
                                <span className={`font-bold ${tx.amount >= 0 ? 'text-emerald-600' : 'text-red-500'} whitespace-nowrap`}>{tx.amount >= 0 ? '+' : ''}{fmtMoney(tx.amount)} EUR</span>
                                <span className="text-[#9CA3AF]">→</span>
                                <span className="font-semibold text-[#111] truncate flex-1 min-w-[120px]">{aiPick.invoice_number}</span>
                                {sug.ai_confidence != null && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-bold">%{sug.ai_confidence}</span>}
                                <button onClick={() => handleApproveSug(sug, aiPick.invoice_id, sug.is_multi_invoice)} disabled={isProc}
                                    className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold rounded-md flex items-center gap-1 disabled:opacity-60"
                                    data-testid={`acc-comp-sug-approve-${sug.id}`}>
                                    {isProc ? <RefreshCw size={10} className="animate-spin" /> : <CheckCircle2 size={10} />}
                                    {isProc ? '...' : 'Onayla'}
                                </button>
                                <button onClick={() => handleRejectSug(sug)} disabled={isProc}
                                    className="h-7 px-2 bg-white border border-red-200 text-red-500 text-[11px] font-semibold rounded-md disabled:opacity-60"
                                    data-testid={`acc-comp-sug-reject-${sug.id}`}>X</button>
                            </div>
                        );
                    })}
                    {data.suggestions.length > 5 && <p className="text-[10px] text-[#6B7280]">... ve {data.suggestions.length - 5} oneri daha (Mutabakat sayfasinda gor)</p>}
                </div>
            )}

            {/* Manuel Eslestirme: tx soldan, fatura sagdan sec */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Sol: Eslesmemis Transferler */}
                <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
                    <div className="px-3 py-2 bg-[#FAFAFA] text-[11px] font-semibold text-[#6B7280] flex items-center justify-between border-b border-[#E5E7EB]">
                        <span>Eslesmemis Transferler ({data.unmatched_transactions.length})</span>
                        {selectedTxId && <button onClick={() => setSelectedTxId(null)} className="text-[10px] text-[#6B7280] hover:text-red-500">Sec iptal</button>}
                    </div>
                    {data.unmatched_transactions.length === 0 ? (
                        <p className="p-6 text-center text-[11px] text-[#9CA3AF]">Eslesmemis transfer yok</p>
                    ) : (
                        <div className="max-h-[400px] overflow-y-auto divide-y divide-[#F3F4F6]">
                            {data.unmatched_transactions.map(t => (
                                <button key={t.id} onClick={() => setSelectedTxId(t.id === selectedTxId ? null : t.id)}
                                    className={`w-full text-left px-3 py-2 hover:bg-[#F9FAFB] ${selectedTxId === t.id ? 'bg-blue-50 ring-1 ring-blue-300' : ''}`}
                                    data-testid={`acc-comp-tx-${t.id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[11px] text-[#6B7280]">{t.transaction_date}</span>
                                        <span className={`text-sm font-bold ${t.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{t.amount >= 0 ? '+' : ''}{fmtMoney(t.amount)}</span>
                                    </div>
                                    {t.communication && <p className="text-[10px] text-[#6B7280] mt-0.5 break-words">{t.communication}</p>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Sag: Acik Faturalar */}
                <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
                    <div className="px-3 py-2 bg-[#FAFAFA] text-[11px] font-semibold text-[#6B7280] flex items-center justify-between border-b border-[#E5E7EB]">
                        <span>Acik Faturalar ({data.open_invoices.length}){selectedInvIds.size > 0 && ` · ${selectedInvIds.size} secili`}</span>
                        {selectedInvIds.size > 0 && <button onClick={() => setSelectedInvIds(new Set())} className="text-[10px] text-[#6B7280] hover:text-red-500">Temizle</button>}
                    </div>
                    {data.open_invoices.length === 0 ? (
                        <p className="p-6 text-center text-[11px] text-[#9CA3AF]">Acik fatura yok</p>
                    ) : (
                        <div className="max-h-[400px] overflow-y-auto divide-y divide-[#F3F4F6]">
                            {data.open_invoices.map(inv => (
                                <label key={inv.id}
                                    className={`block px-3 py-2 hover:bg-[#F9FAFB] cursor-pointer ${selectedInvIds.has(inv.id) ? 'bg-emerald-50' : ''}`}
                                    data-testid={`acc-comp-inv-${inv.id}`}>
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" checked={selectedInvIds.has(inv.id)} onChange={() => toggleInv(inv.id)}
                                            className="w-3.5 h-3.5 accent-emerald-600" data-testid={`acc-comp-inv-cb-${inv.id}`} />
                                        <span className="text-[12px] font-medium text-[#111] flex-1 truncate">{inv.invoice_number}</span>
                                        <span className={`text-[9px] px-1 rounded ${inv.type === 'sales' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>{inv.type === 'sales' ? 'S' : 'A'}</span>
                                        <span className="text-sm font-bold text-[#111]">{fmtMoney(inv.remaining)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#9CA3AF] pl-5">
                                        <span>{inv.invoice_date}</span>
                                        {inv.due_date && <span>vade: {inv.due_date}</span>}
                                        <span>tutar: {fmtMoney(inv.total)}</span>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Eslestir butonu */}
            {(selectedTxId || selectedInvIds.size > 0) && (
                <div className="bg-[#111827] text-white rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="acc-comp-recon-action">
                    <div className="flex items-center gap-3 flex-wrap text-[12px]">
                        <span>Transfer: <strong>{selectedTx ? `${selectedTx.amount >= 0 ? '+' : ''}${fmtMoney(selectedTx.amount)} EUR` : '— sec —'}</strong></span>
                        <span>↔</span>
                        <span>Fatura: <strong>{selectedInvIds.size > 0 ? `${selectedInvIds.size} × ${fmtMoney(sumSelInv)} EUR` : '— sec —'}</strong></span>
                        {selectedTx && selectedInvIds.size > 0 && (
                            <span className={`text-[10px] px-2 py-0.5 rounded ${Math.abs(txAmt - sumSelInv) < 0.5 ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>
                                Fark: {fmtMoney(Math.abs(txAmt - sumSelInv))} EUR
                            </span>
                        )}
                    </div>
                    <button onClick={handleManualMatch} disabled={matching || !selectedTxId || selectedInvIds.size === 0}
                        className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                        data-testid="acc-comp-match-btn">
                        {matching ? <RefreshCw size={12} className="animate-spin" /> : <Layers size={12} />}
                        {matching ? 'Eslestiriliyor...' : 'Eslestir'}
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── BANK COMPANY DETAIL MODAL (Transferler + Birlestirme onerileri) ───
function BankCompanyDetailModal({ companyId, onClose, onMerged }) {
    const [data, setData] = useState(null);
    const [transfers, setTransfers] = useState([]);
    const [transTotal, setTransTotal] = useState({ total_in: 0, total_out: 0, total: 0 });
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [merging, setMerging] = useState(null);   // target_id
    const [tab, setTab] = useState('transfers');    // 'transfers' | 'merge'

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const [tRes, mRes] = await Promise.all([
                    axios.get(`${API}/api/accounting/companies/${companyId}/transfers?limit=200`, { headers: headers() }),
                    axios.get(`${API}/api/accounting/companies/${companyId}/merge-candidates`, { headers: headers() }),
                ]);
                if (!alive) return;
                setTransfers(tRes.data.items || []);
                setTransTotal({ total_in: tRes.data.total_in, total_out: tRes.data.total_out, total: tRes.data.total });
                setData(mRes.data.source);
                setCandidates(mRes.data.candidates || []);
            } catch { toast.error('Detay yuklenemedi'); }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [companyId]);

    const handleMerge = async (target) => {
        if (!window.confirm(`'${data.name}' firmasini\n'${target.name}' firmasiyla birlestirmek istiyor musun?\n\nTum banka transferleri ve faturalar hedef firmaya tasinir, kaynak silinir.`)) return;
        setMerging(target.id);
        try {
            const r = await axios.post(`${API}/api/accounting/companies/merge`,
                { source_id: companyId, target_id: target.id }, { headers: headers() });
            toast.success(`Birlestirildi: ${r.data.txns_repointed} transfer, ${r.data.invoices_repointed} fatura tasindi`, { duration: 6000 });
            onMerged && onMerged();
        } catch (e) {
            const detail = e.response?.data?.detail || 'Hata';
            toast.error(`Birlestirme hatasi: ${detail}`, { duration: 6000 });
        } finally { setMerging(null); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="acc-bank-company-detail">
                {loading ? <div className="p-12 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div> :
                    !data ? <div className="p-12 text-center">Bulunamadi</div> : (
                        <>
                            <div className="px-6 py-4 border-b border-[#F3F4F6] flex items-start justify-between gap-4 shrink-0">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h2 className="text-lg font-bold text-[#111] truncate">{data.name || '(isimsiz)'}</h2>
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] font-semibold">Bankadan</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-1 text-[11px] text-[#6B7280]">
                                        {data.vat_number && <span>KDV: {data.vat_number}</span>}
                                        {data.iban && <span>IBAN: {data.iban}</span>}
                                    </div>
                                </div>
                                <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111] shrink-0"><X size={20} /></button>
                            </div>

                            {/* Mini KPIs */}
                            <div className="px-6 py-3 border-b border-[#F3F4F6] grid grid-cols-3 gap-3 shrink-0">
                                <DetailKpi label="Toplam Transfer" value={transTotal.total} color="text-[#111]" testId="acc-bank-kpi-total" />
                                <DetailKpi label="Gelen Para" value={`+${fmtMoney(transTotal.total_in)} EUR`} color="text-emerald-600" testId="acc-bank-kpi-in" />
                                <DetailKpi label="Giden Para" value={`-${fmtMoney(transTotal.total_out)} EUR`} color="text-red-500" testId="acc-bank-kpi-out" />
                            </div>

                            {/* Tabs */}
                            <div className="px-6 pt-2 border-b border-[#F3F4F6] flex gap-1 shrink-0">
                                <button onClick={() => setTab('transfers')}
                                    className={`px-4 py-2 text-xs font-semibold border-b-2 ${tab === 'transfers' ? 'border-[#111] text-[#111]' : 'border-transparent text-[#9CA3AF]'}`}
                                    data-testid="acc-bank-tab-transfers">
                                    Banka Transferleri ({transTotal.total})
                                </button>
                                <button onClick={() => setTab('merge')}
                                    className={`px-4 py-2 text-xs font-semibold border-b-2 flex items-center gap-1 ${tab === 'merge' ? 'border-[#8B5CF6] text-[#8B5CF6]' : 'border-transparent text-[#9CA3AF]'}`}
                                    data-testid="acc-bank-tab-merge">
                                    <Sparkles size={11} />Birlestirme Onerileri ({candidates.length})
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto">
                                {tab === 'transfers' ? (
                                    transfers.length === 0 ? <div className="p-12 text-center text-sm text-[#9CA3AF]">Transfer bulunamadi</div> :
                                        <table className="w-full text-sm">
                                            <thead className="bg-[#FAFAFA] sticky top-0 z-10">
                                                <tr className="text-[10px] text-[#9CA3AF] uppercase">
                                                    <th className="text-left px-4 py-2 font-semibold">Tarih</th>
                                                    <th className="text-left px-4 py-2 font-semibold">Aciklama</th>
                                                    <th className="text-right px-4 py-2 font-semibold">Tutar</th>
                                                    <th className="text-center px-4 py-2 font-semibold">Durum</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[#F3F4F6]">
                                                {transfers.map(t => (
                                                    <tr key={t.id} className="hover:bg-[#F9FAFB]" data-testid={`acc-bank-tx-${t.id}`}>
                                                        <td className="px-4 py-2 text-xs text-[#6B7280] whitespace-nowrap">{t.transaction_date}</td>
                                                        <td className="px-4 py-2 text-[12px] text-[#374151] break-words">{t.communication || t.reference || '—'}</td>
                                                        <td className={`px-4 py-2 text-right font-bold ${t.amount >= 0 ? 'text-emerald-600' : 'text-red-500'} whitespace-nowrap`}>
                                                            {t.amount >= 0 ? '+' : ''}{fmtMoney(t.amount)}
                                                        </td>
                                                        <td className="px-4 py-2 text-center">
                                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${t.status === 'matched' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                                                {t.status === 'matched' ? 'Eslesmis' : 'Eslesmeyen'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                ) : (
                                    candidates.length === 0 ? (
                                        <div className="p-12 text-center text-sm text-[#9CA3AF]">
                                            <Sparkles size={20} className="mx-auto mb-2 text-[#9CA3AF]" />
                                            Benzer firma bulunamadi.<br /><span className="text-[10px]">Bu firma muhtemelen yeni / bagimsiz.</span>
                                        </div>
                                    ) : (
                                        <div className="p-4 space-y-2">
                                            <p className="text-[11px] text-[#6B7280] mb-2">
                                                AI bu firmaya benzer isim/IBAN'a sahip mevcut firmalari buldu. Birlestirmek icin tikla.
                                            </p>
                                            {candidates.map(c => (
                                                <div key={c.id} className="border border-[#E5E7EB] rounded-xl p-3 hover:bg-[#FAFAFA]" data-testid={`acc-merge-cand-${c.id}`}>
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-sm font-semibold text-[#111] truncate">{c.name}</span>
                                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${c.score >= 0.95 ? 'bg-emerald-100 text-emerald-700' : c.score >= 0.85 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                                                                    %{Math.round(c.score * 100)}
                                                                </span>
                                                                {c.roles?.map(r => <span key={r} className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${r === 'customer' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>{r === 'customer' ? 'Musteri' : 'Tedarikci'}</span>)}
                                                            </div>
                                                            <div className="flex flex-wrap gap-2 mt-1 text-[10px] text-[#9CA3AF]">
                                                                {c.vat_number && <span>KDV: {c.vat_number}</span>}
                                                                {c.iban && <span>IBAN: {c.iban}</span>}
                                                                <span className="text-[#6B7280] font-semibold">{c.invoice_count} fatura</span>
                                                            </div>
                                                            <p className="text-[10px] text-[#8B5CF6] italic mt-1">{c.reason}</p>
                                                        </div>
                                                        <button onClick={() => handleMerge(c)} disabled={merging === c.id || !!merging}
                                                            className="h-8 px-3 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs font-semibold rounded-lg flex items-center gap-1 disabled:opacity-50 shrink-0"
                                                            data-testid={`acc-merge-btn-${c.id}`}>
                                                            {merging === c.id ? <RefreshCw size={11} className="animate-spin" /> : <Layers size={11} />}
                                                            {merging === c.id ? 'Birlesiyor...' : 'Birlestir'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )
                                )}
                            </div>
                        </>
                    )}
            </div>
        </div>
    );
}

const STATUS_COLORS = { open: 'bg-blue-50 text-blue-700', partially_paid: 'bg-amber-50 text-amber-700', paid: 'bg-emerald-50 text-emerald-700', overdue: 'bg-red-50 text-red-700', cancelled: 'bg-gray-100 text-gray-500', draft: 'bg-gray-50 text-gray-500' };
const STATUS_LABELS = { open: 'Acik', partially_paid: 'Kismi', paid: 'Odenmis', overdue: 'Vadesi Gecmis', cancelled: 'Iptal', draft: 'Taslak' };
function StatusBadge({ status }) {
    return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-500'}`}>{STATUS_LABELS[status] || status}</span>;
}

// ─── INVOICES ───
function AccInvoices() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dupOnly, setDupOnly] = useState(false);
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [importing, setImporting] = useState(false);
    const [showOcr, setShowOcr] = useState(false);
    const [ocrPrefill, setOcrPrefill] = useState(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [contactFilter, setContactFilter] = useState(null);   // {id, name}
    const [companySearchOpen, setCompanySearchOpen] = useState(false);
    const [companyResults, setCompanyResults] = useState([]);
    const [companyQuery, setCompanyQuery] = useState('');

    const fetch = useCallback(() => {
        setLoading(true);
        const p = new URLSearchParams();
        if (typeFilter) p.set('type', typeFilter);
        if (statusFilter) p.set('status', statusFilter);
        if (search) p.set('q', search);
        if (dateFrom) p.set('date_from', dateFrom);
        if (dateTo) p.set('date_to', dateTo);
        if (contactFilter?.id) p.set('contact_id', contactFilter.id);
        if (dupOnly) p.set('dup_only', 'true');
        p.set('page', String(page)); p.set('limit', String(limit));
        axios.get(`${API}/api/accounting/invoices?${p}`, { headers: headers() })
            .then(r => {
                setItems(r.data.items || []);
                setTotal(r.data.total || 0);
            })
            .catch(() => toast.error('Yuklenemedi'))
            .finally(() => setLoading(false));
    }, [typeFilter, statusFilter, search, page, limit, dupOnly, dateFrom, dateTo, contactFilter]);
    useEffect(() => { fetch(); }, [fetch]);
    useEffect(() => { setPage(1); }, [typeFilter, statusFilter, search, dupOnly, dateFrom, dateTo, contactFilter]);

    // Company search (debounced) - filter modal
    useEffect(() => {
        if (!companySearchOpen) return;
        if (!companyQuery || companyQuery.length < 2) { setCompanyResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await axios.get(`${API}/api/accounting/companies?q=${encodeURIComponent(companyQuery)}&limit=15`, { headers: headers() });
                setCompanyResults(r.data.items || []);
            } catch { /* silent */ }
        }, 250);
        return () => clearTimeout(t);
    }, [companyQuery, companySearchOpen]);

    const handleBillitImport = async () => {
        setImporting(true);
        try {
            const r = await axios.post(`${API}/api/accounting/invoices/import-billit`, {}, { headers: headers() });
            const unk = r.data.unknown_order_types || {};
            const unkKeys = Object.keys(unk);
            const fixedStr = r.data.fixed > 0 ? ` · ${r.data.fixed} kayit type duzeltildi` : '';
            const linkedStr = r.data.credit_notes_linked > 0 ? ` · ${r.data.credit_notes_linked} iade orijinal faturaya baglandi` : '';
            const baseMsg = `${r.data.created} fatura eklendi, ${r.data.skipped} atlandi${fixedStr}${linkedStr}, ${r.data.companies_created} firma olusturuldu`;
            const skipMsg = r.data.skipped_unknown_type > 0
                ? ` · ${r.data.skipped_unknown_type} bilinmeyen tip: ${unkKeys.slice(0, 3).join(', ')}${unkKeys.length > 3 ? '...' : ''}`
                : '';
            toast.success(baseMsg + skipMsg, { duration: 7000 });
            fetch();
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail;
            const msg = status === 404
                ? `Billit endpoint bulunamadi (404). Backend deploy guncel olmayabilir.`
                : status === 400 && detail
                ? detail
                : detail
                ? `${detail} (HTTP ${status || '?'})`
                : `Import hatasi (HTTP ${status || 'baglanti'})`;
            toast.error(msg, { duration: 8000 });
        }
        finally { setImporting(false); }
    };

    // ─── Excel Export / Import ─────────────────────────────────────────────
    const [excelLoading, setExcelLoading] = useState(false);
    const excelImportInputRef = React.useRef(null);

    const handleExcelExport = async () => {
        setExcelLoading(true);
        try {
            const r = await axios.get(`${API}/api/accounting/invoices/export.xlsx`, {
                headers: headers(),
                responseType: 'blob',
                timeout: 120000,
            });
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url;
            const now = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '_');
            a.download = `flowbil_invoices_${now}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            const total = r.headers['x-total-rows'] || '?';
            toast.success(`${total} fatura indirildi`);
        } catch (e) {
            toast.error(`Export hatasi: ${e.response?.data?.detail || e.message}`);
        } finally { setExcelLoading(false); }
    };

    const handleExcelTemplate = async () => {
        try {
            const r = await axios.get(`${API}/api/accounting/invoices/import-template.xlsx`, {
                headers: headers(),
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'flowbil_import_template.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error('Sablon indirilemedi');
        }
    };

    const handleExcelImport = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!file.name.match(/\.(xlsx|xlsm)$/i)) {
            toast.error('Sadece .xlsx dosyalari kabul edilir');
            return;
        }
        // Side secimi: dosya adi 'aankop'/'alis'/'purchase' iceriyorsa purchase, aksi sales
        const lower = file.name.toLowerCase();
        const guessPurchase = /aankop|alis|alış|purchase|inkoop/.test(lower);
        const guessSales = /verkop|satis|satış|sales|verkoop/.test(lower);
        let side;
        if (guessPurchase && !guessSales) side = 'purchase';
        else if (guessSales && !guessPurchase) side = 'sales';
        else {
            // Belirsiz — kullaniciya sor
            const ans = window.prompt(
                `"${file.name}" - Bu dosya ne tip faturalar iceriyor?\n\n` +
                `1 = Satis faturalari (Verkopen)\n` +
                `2 = Alis faturalari (Aankopen)\n\n` +
                `1 veya 2 yaz:`, '1'
            );
            if (ans === null) return;
            if (ans.trim() === '2') side = 'purchase';
            else if (ans.trim() === '1') side = 'sales';
            else { toast.error('Gecersiz secim'); return; }
        }
        const sideLabel = side === 'purchase' ? 'ALIS' : 'SATIS';
        if (!window.confirm(`"${file.name}" icindeki ${sideLabel} faturalarini Import Onizleme'ye al?\n\nVeriler DB'ye DOGRUDAN yazilmaz - once anomaliler (duplicate, fazla odeme, benzer firma vb.) listelenir, sen karar verirsin.`)) return;
        setExcelLoading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await axios.post(`${API}/api/accounting/imports/invoices/stage?side=${side}`, fd, {
                headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
                timeout: 180000,
            });
            const d = r.data;
            toast.success(`${d.stats.total} satir staging'e alindi (${d.stats.flagged + d.stats.error} suphe/hata)`, { duration: 5000 });
            navigate(`/accounting/imports/${d.batch_id}`);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(`Import hatasi: ${typeof detail === 'string' ? detail : (detail?.message || err.message)}`, { duration: 8000 });
        } finally { setExcelLoading(false); }
    };

    const handleBackfillCnLinks = async () => {
        if (!window.confirm('Tum credit note (iade) faturalarini Billit\'ten yeniden tarayip orijinal faturalara baglamayi deneyelim mi? Bu islem 1-2 dakika surebilir.')) return;
        try {
            const r = await axios.post(`${API}/api/accounting/invoices/credit-notes/backfill-links`, {}, { headers: headers() });
            const d = r.data;
            toast.success(`${d.linked}/${d.total_credit_notes} iade orijinal faturaya baglandi · ${d.no_billit_ref} kaydin Billit'te referansi yok · ${d.ref_found_but_no_original_in_db} ref var ama orijinal DB'de bulunamadi`, { duration: 8000 });
            fetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Backfill hatasi');
        }
    };

    const handleDelete = async (id) => {
        try { await axios.delete(`${API}/api/accounting/invoices/${id}`, { headers: headers() }); fetch(); toast.success('Silindi'); }
        catch { toast.error('Silinemedi'); }
    };

    return (
        <div className="space-y-3" data-testid="acc-invoices">
            {/* === Sticky Toolbar - sade 2 satir === */}
            {/* Ust satir: arama + filtreler + ana CTA'lar */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Fatura no, firma ara..."
                        className="w-full h-9 pl-9 pr-3 bg-white border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" data-testid="acc-invoice-search" />
                </div>
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="h-9 px-3 bg-white border border-[#E5E7EB] rounded-xl text-sm" data-testid="acc-invoice-type-filter">
                    <option value="">Tum Tipler</option>
                    <option value="sales">Satis</option>
                    <option value="purchase">Alis</option>
                    <option value="sales_credit">Satis Iade</option>
                    <option value="purchase_credit">Alis Iade</option>
                </select>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 px-3 bg-white border border-[#E5E7EB] rounded-xl text-sm" data-testid="acc-invoice-status-filter">
                    <option value="">Tum Durumlar</option>
                    <option value="open">Acik</option>
                    <option value="partially_paid">Kismi</option>
                    <option value="paid">Odenmis</option>
                    <option value="overdue">Gecikmis</option>
                </select>
                <button onClick={() => setDupOnly(d => !d)}
                    className={`h-9 px-3 text-xs font-semibold rounded-xl flex items-center gap-1 ${dupOnly ? 'bg-amber-500 text-white' : 'bg-white border border-[#E5E7EB] text-[#6B7280]'}`}
                    data-testid="acc-invoice-dup-toggle"
                    title="Sadece duplicate fatura no olan kayitlari goster">
                    <AlertTriangle size={11} />Duplicate
                </button>
                {/* Ana CTA sag tarafta - en onemli aksiyonlar */}
                <div className="ml-auto flex items-center gap-2">
                    <button
                        onClick={() => window.open('/accounting/ocr-batch', '_blank', 'noopener,noreferrer')}
                        className="h-9 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-xl flex items-center gap-1.5 shadow-sm hover:shadow-md transition-shadow"
                        data-testid="acc-ocr-batch-open-btn"
                        title="Toplu OCR'i yeni sekmede ac"
                    >
                        <Sparkles size={13} />Toplu OCR
                    </button>
                    <button
                        onClick={() => setShowOcr(true)}
                        className="h-9 px-4 bg-[#111] hover:bg-black text-white text-sm font-semibold rounded-xl flex items-center gap-1.5"
                        data-testid="acc-add-invoice-btn"
                        title="Tek fatura - manuel veya OCR"
                    >
                        <Plus size={14} />Yeni Fatura
                    </button>
                </div>
            </div>

            {/* Alt satir: Import / Export / Billit araclari (gruplu, daha sade) */}
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] bg-[#FAFAFA] border border-[#F3F4F6] rounded-xl px-3 py-2">
                <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mr-1">Import / Export:</span>
                <button onClick={handleBillitImport} disabled={importing}
                    className="h-7 px-2.5 bg-[#8B5CF6]/10 hover:bg-[#8B5CF6] hover:text-white text-[#8B5CF6] font-semibold rounded-md flex items-center gap-1 disabled:opacity-50 transition-colors"
                    data-testid="acc-billit-import-btn">
                    <RefreshCw size={10} className={importing ? 'animate-spin' : ''} />Billit
                </button>
                <button onClick={handleBackfillCnLinks}
                    className="h-7 px-2.5 bg-rose-50 hover:bg-rose-500 hover:text-white border border-rose-200 text-rose-700 font-semibold rounded-md flex items-center gap-1 transition-colors"
                    data-testid="acc-cn-backfill-btn"
                    title="Iade faturalarini Billit'ten tekrar tarayip orijinal faturalara bagla">
                    <Link2 size={10} />Iade Linkle
                </button>
                <span className="w-px h-4 bg-[#E5E7EB] mx-1" />
                <button onClick={handleExcelExport} disabled={excelLoading}
                    className="h-7 px-2.5 bg-emerald-50 hover:bg-emerald-500 hover:text-white border border-emerald-200 text-emerald-700 font-semibold rounded-md flex items-center gap-1 disabled:opacity-50 transition-colors"
                    data-testid="acc-excel-export-btn"
                    title="Tum faturalari Excel olarak indir">
                    <FileDown size={10} />Excel Indir
                </button>
                <input ref={excelImportInputRef} type="file" accept=".xlsx,.xlsm"
                    onChange={handleExcelImport} className="hidden" data-testid="acc-excel-import-input" />
                <button onClick={() => excelImportInputRef.current?.click()} disabled={excelLoading}
                    className="h-7 px-2.5 bg-blue-50 hover:bg-blue-500 hover:text-white border border-blue-200 text-blue-700 font-semibold rounded-md flex items-center gap-1 disabled:opacity-50 transition-colors"
                    data-testid="acc-excel-import-btn"
                    title="Excel'den toplu fatura import et">
                    <Upload size={10} />Excel Yukle
                </button>
                <button onClick={handleExcelTemplate}
                    className="h-7 px-2 bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] text-[#6B7280] font-medium rounded-md transition-colors"
                    data-testid="acc-excel-template-btn"
                    title="Bos Excel sablonu indir">
                    Sablon
                </button>
            </div>

            {/* Date + Company filter satiri */}
            <div className="flex flex-wrap items-center gap-2" data-testid="acc-invoice-filters-row2">
                <div className="flex items-center gap-1.5 bg-white border border-[#E5E7EB] rounded-xl px-2 h-9">
                    <Calendar size={12} className="text-[#9CA3AF]" />
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                        className="h-7 text-xs outline-none w-32" data-testid="acc-invoice-date-from" />
                    <span className="text-[10px] text-[#9CA3AF]">→</span>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                        className="h-7 text-xs outline-none w-32" data-testid="acc-invoice-date-to" />
                    {(dateFrom || dateTo) && (
                        <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-[#9CA3AF] hover:text-red-500" title="Tarih filtresini temizle">
                            <X size={11} />
                        </button>
                    )}
                </div>
                {contactFilter ? (
                    <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-xl px-3 h-9 text-xs" data-testid="acc-invoice-contact-filter-chip">
                        <Building2 size={12} className="text-emerald-700" />
                        <span className="font-semibold text-emerald-800 max-w-[200px] truncate">{contactFilter.name}</span>
                        <button onClick={() => setContactFilter(null)} className="text-emerald-700 hover:text-red-500">
                            <X size={11} />
                        </button>
                    </div>
                ) : (
                    <button onClick={() => { setCompanySearchOpen(true); setCompanyQuery(''); }}
                        className="h-9 px-3 bg-white border border-[#E5E7EB] hover:border-[#8B5CF6] text-[#6B7280] text-xs rounded-xl flex items-center gap-1.5"
                        data-testid="acc-invoice-contact-filter-btn">
                        <Building2 size={11} />Firma filtresi
                    </button>
                )}
                {(dateFrom || dateTo || contactFilter || typeFilter || statusFilter || search) && (
                    <button onClick={() => { setDateFrom(''); setDateTo(''); setContactFilter(null); setTypeFilter(''); setStatusFilter(''); setSearch(''); setDupOnly(false); }}
                        className="h-9 px-2 text-[10px] text-red-500 hover:underline" data-testid="acc-invoice-clear-filters-btn">
                        Tum filtreleri temizle
                    </button>
                )}
            </div>

            {companySearchOpen && (
                <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 pt-20" onClick={() => setCompanySearchOpen(false)}>
                    <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-3" data-testid="acc-invoice-company-search-modal">
                        <input autoFocus value={companyQuery} onChange={e => setCompanyQuery(e.target.value)}
                            placeholder="Firma adi, IBAN veya KDV ara..." className="w-full h-9 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-sm mb-2"
                            data-testid="acc-invoice-company-search-input" />
                        <div className="max-h-80 overflow-y-auto">
                            {companyResults.length === 0 && companyQuery.length >= 2 && <p className="text-xs text-[#9CA3AF] p-3">Sonuc yok</p>}
                            {companyResults.map(c => (
                                <button key={c.id} onClick={() => { setContactFilter({ id: c.id, name: c.name }); setCompanySearchOpen(false); }}
                                    className="w-full text-left px-3 py-2 hover:bg-[#F9FAFB] rounded-lg" data-testid={`acc-invoice-company-pick-${c.id}`}>
                                    <div className="text-sm font-semibold text-[#111]">{c.name}</div>
                                    <div className="text-[10px] text-[#9CA3AF]">{c.iban || c.vat_number || ''}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                {loading ? <div className="p-8 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div> :
                    items.length === 0 ? <div className="p-8 text-center text-sm text-[#9CA3AF]">Fatura bulunamadi</div> : (
                        <>
                            <div className="hidden md:grid grid-cols-[1fr_90px_100px_140px_100px_90px_90px_50px] px-4 py-2 border-b border-[#F3F4F6] bg-[#FAFAFA] gap-2">
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase">Fatura / Aciklama</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase">Tip</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase">Tarih</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase">Odeme Ref</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase text-right">Tutar</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase text-right">Odenen</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase text-center">Durum</span>
                                <span></span>
                            </div>
                            <div className="divide-y divide-[#F3F4F6]">
                                {items.map(inv => (
                                    <div key={inv.id} className={`md:grid md:grid-cols-[1fr_90px_100px_140px_100px_90px_90px_50px] flex flex-col gap-1 md:gap-2 px-4 py-2.5 hover:bg-[#F9FAFB] md:items-center ${inv.is_duplicate ? 'bg-amber-50/40' : ''}`} data-testid={`acc-inv-${inv.id}`}>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <button
                                                    onClick={() => navigate(`/accounting/invoices/${inv.id}`)}
                                                    className="text-sm font-medium text-[#111] break-words hover:text-blue-600 hover:underline text-left"
                                                    data-testid={`acc-inv-open-${inv.id}`}>
                                                    {inv.invoice_number}
                                                </button>
                                                {inv.is_duplicate && (
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500 text-white font-bold flex items-center gap-0.5" title="Bu fatura numarasi birden fazla kaydedilmis" data-testid={`acc-inv-dup-${inv.id}`}>
                                                        <AlertTriangle size={8} />DUPLICATE
                                                    </span>
                                                )}
                                                {(inv.type || '').includes('credit') && (
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500 text-white font-bold">IADE</span>
                                                )}
                                                {(inv.type || '').includes('credit') && inv.linked_invoice_number && (
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-bold border border-rose-200" title={'Orijinal fatura: ' + inv.linked_invoice_number + (inv.linked_invoice_id ? '' : ' (DB de bulunamadi)')} data-testid={`acc-inv-linked-${inv.id}`}>
                                                        → #{inv.linked_invoice_number}{!inv.linked_invoice_id && ' ?'}
                                                    </span>
                                                )}
                                            </div>
                                            {inv.contact_id ? (
                                                <button onClick={(e) => { e.stopPropagation(); navigate(`/accounting/companies/${inv.contact_id}`); }}
                                                    className="text-[11px] text-blue-600 hover:underline hover:text-blue-700 block break-words text-left font-medium"
                                                    data-testid={`acc-inv-contact-${inv.id}`}>
                                                    {inv.contact_name}
                                                </button>
                                            ) : (
                                                <span className="text-[11px] text-[#6B7280] block break-words">{inv.contact_name}</span>
                                            )}
                                            {inv.notes && <span className="text-[10px] text-[#9CA3AF] block break-words mt-0.5 italic">"{inv.notes}"</span>}
                                        </div>
                                        <span className={`text-xs font-medium ${inv.type === 'sales' || inv.type === 'sales_credit' ? 'text-emerald-600' : 'text-blue-600'}`}>
                                            {inv.type === 'sales' ? 'Satis' : inv.type === 'purchase' ? 'Alis' : inv.type === 'sales_credit' ? 'Satis Iade' : inv.type === 'purchase_credit' ? 'Alis Iade' : inv.type}
                                        </span>
                                        <span className="text-xs text-[#6B7280]">{inv.invoice_date}</span>
                                        <span className="text-[10px] text-[#6B7280] font-mono break-all">{inv.payment_reference || '—'}</span>
                                        <span className="text-xs font-bold text-[#111] text-right">{fmtMoney(inv.total)}</span>
                                        <span className="text-xs text-[#6B7280] text-right">{fmtMoney(inv.paid_amount)}</span>
                                        <div className="text-center"><StatusBadge status={inv.status} /></div>
                                        <button onClick={() => handleDelete(inv.id)} className="w-6 h-6 rounded flex items-center justify-center text-[#D1D5DB] hover:text-red-500"><Trash2 size={12} /></button>
                                    </div>
                                ))}
                            </div>
                            <Pagination page={page} total={total} limit={limit} onPageChange={setPage} testId="acc-invoices-pagination" />
                        </>
                    )}
            </div>
            {showForm && <InvoiceForm initial={ocrPrefill} onClose={() => { setShowForm(false); setOcrPrefill(null); }} onSaved={() => { setShowForm(false); setOcrPrefill(null); fetch(); }} />}
            {showOcr && <OcrUploadModal onClose={() => setShowOcr(false)} onSaved={() => fetch()} />}
        </div>
    );
}

function InvoiceForm({ onClose, onSaved, initial }) {
    const [form, setForm] = useState(() => ({
        type: initial?.type || 'sales',
        invoice_number: initial?.invoice_number || '',
        invoice_date: initial?.invoice_date || new Date().toISOString().slice(0, 10),
        due_date: initial?.due_date || '',
        contact_name: initial?.contact_name || '',
        vat_number: initial?.vat_number || '',
        iban: initial?.iban || '',
        subtotal: initial?.subtotal ?? 0,
        vat_amount: initial?.vat_amount ?? 0,
        total: initial?.total ?? 0,
        payment_reference: initial?.payment_reference || '',
        notes: initial?._ocr ? `OCR ile yuklendi (${initial._ocr.filename})` : '',
    }));
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const handleSubmit = async (e) => {
        e.preventDefault(); setSaving(true);
        const payload = { ...form, subtotal: parseFloat(form.subtotal) || 0, vat_amount: parseFloat(form.vat_amount) || 0, total: parseFloat(form.total) || 0 };
        try {
            const r = await axios.post(`${API}/api/accounting/invoices`, payload, { headers: headers() });
            const extras = [];
            if (r.data?._auto_company_created) extras.push('firma olusturuldu');
            const rc = r.data?._recon;
            if (rc?.auto_matched > 0) extras.push(`${rc.auto_matched} banka transferi eslesti`);
            else if (rc?.pending_suggestions > 0) extras.push(`${rc.pending_suggestions} oneri olusturuldu`);
            toast.success('Fatura eklendi' + (extras.length ? ' - ' + extras.join(', ') : ''));
            onSaved();
        } catch (e) {
            const detail = e.response?.data?.detail;
            // Duplicate hatasi - kullaniciya 'yine de kaydet' secenegi sun
            if (e.response?.status === 409 && detail?.error === 'duplicate_invoice') {
                if (window.confirm(`${detail.message}\n\nYine de kaydedeyim mi? (Duplicate olarak isaretlenecek)`)) {
                    try {
                        await axios.post(`${API}/api/accounting/invoices`, { ...payload, force_duplicate: true }, { headers: headers() });
                        toast.success('Fatura eklendi (duplicate)'); onSaved();
                    } catch (e2) { toast.error(e2.response?.data?.detail || 'Hata'); }
                }
            } else {
                toast.error(typeof detail === 'string' ? detail : detail?.message || 'Hata');
            }
        } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
            <form onSubmit={handleSubmit} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="acc-invoice-form">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-bold text-[#111]">Yeni Fatura</h3>
                    <button type="button" onClick={onClose} className="text-[#9CA3AF] hover:text-[#111]"><X size={18} /></button>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                    {[['sales', 'Satis Faturasi'], ['purchase', 'Alis Faturasi'], ['sales_credit', 'Satis Iade'], ['purchase_credit', 'Alis Iade']].map(([t, lbl]) => (
                        <button key={t} type="button" onClick={() => set('type', t)} className={`h-9 rounded-xl text-xs font-medium ${form.type === t ? (t.includes('credit') ? 'bg-rose-500 text-white' : 'bg-[#111827] text-white') : 'bg-[#F3F4F6] text-[#6B7280]'}`}>{lbl}</button>
                    ))}
                </div>
                <input value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} placeholder="Fatura No *" required className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" data-testid="acc-inv-number-input" />
                <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[10px] text-[#9CA3AF]">Fatura Tarihi</label><input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" /></div>
                    <div><label className="text-[10px] text-[#9CA3AF]">Vade Tarihi</label><input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" /></div>
                </div>
                <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Firma Adi" className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" />
                <div className="grid grid-cols-3 gap-2">
                    <div><label className="text-[10px] text-[#9CA3AF]">Ara Toplam</label><input type="number" step="0.01" value={form.subtotal} onChange={e => { const v = e.target.value; set('subtotal', v); set('total', (parseFloat(v) || 0) + (parseFloat(form.vat_amount) || 0)); }} className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" /></div>
                    <div><label className="text-[10px] text-[#9CA3AF]">KDV</label><input type="number" step="0.01" value={form.vat_amount} onChange={e => { const v = e.target.value; set('vat_amount', v); set('total', (parseFloat(form.subtotal) || 0) + (parseFloat(v) || 0)); }} className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm" /></div>
                    <div><label className="text-[10px] text-[#9CA3AF]">Toplam</label><input type="number" step="0.01" value={form.total} onChange={e => set('total', e.target.value)} className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm font-bold" /></div>
                </div>
                <input value={form.payment_reference} onChange={e => set('payment_reference', e.target.value)} placeholder="Odeme Referansi / Yapisal Iletisim" className="w-full h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" />
                <button type="submit" disabled={saving} className="w-full h-10 bg-[#111827] text-white text-sm font-semibold rounded-xl disabled:opacity-50" data-testid="acc-inv-save-btn">
                    {saving ? 'Kaydediliyor...' : 'Fatura Ekle'}
                </button>
            </form>
        </div>
    );
}

// OCR/Manuel fatura modali ayri dosyada (accounting/OcrUploadModal.js)



const CAT_LABELS = {
    vergi_sgk: 'Vergi/SGK', banka_masrafi: 'Banka Masrafi', maas: 'Maas',
    kira: 'Kira', kredi_taksit: 'Kredi/Taksit', ceza: 'Ceza', iade: 'Iade',
    transfer_ici: 'Sirket Ici', fatura_odemesi: 'Fatura Odemesi',
    tahsilat: 'Tahsilat', diger_gider: 'Diger Gider', diger_gelir: 'Diger Gelir',
};
const CAT_COLORS = {
    vergi_sgk: 'bg-rose-50 text-rose-700', banka_masrafi: 'bg-slate-100 text-slate-700',
    maas: 'bg-blue-50 text-blue-700', kira: 'bg-amber-50 text-amber-700',
    kredi_taksit: 'bg-purple-50 text-purple-700', ceza: 'bg-red-50 text-red-700',
    iade: 'bg-cyan-50 text-cyan-700', transfer_ici: 'bg-indigo-50 text-indigo-700',
    fatura_odemesi: 'bg-emerald-50 text-emerald-700', tahsilat: 'bg-emerald-50 text-emerald-700',
    diger_gider: 'bg-gray-100 text-gray-600', diger_gelir: 'bg-gray-100 text-gray-600',
};

function AccBank() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [direction, setDirection] = useState('');   // '' | 'in' | 'out'
    const [categoryFilter, setCategoryFilter] = useState('');
    const [uploading, setUploading] = useState(false);
    const [aiCatRunning, setAiCatRunning] = useState(false);
    const [catEditTx, setCatEditTx] = useState(null);
    const [companyDetailId, setCompanyDetailId] = useState(null);
    const [resolvingCompany, setResolvingCompany] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkCat, setBulkCat] = useState('');
    const [bulkRunning, setBulkRunning] = useState(false);
    const [unlinkingId, setUnlinkingId] = useState(null);

    const fetch = useCallback(() => {
        setLoading(true);
        const p = new URLSearchParams();
        if (statusFilter) p.set('status', statusFilter);
        if (direction) p.set('direction', direction);
        if (categoryFilter) p.set('category', categoryFilter);
        if (search) p.set('q', search);
        p.set('page', String(page)); p.set('limit', String(limit));
        axios.get(`${API}/api/accounting/bank-transactions?${p}`, { headers: headers() })
            .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
            .catch(() => toast.error('Yuklenemedi'))
            .finally(() => setLoading(false));
    }, [search, statusFilter, direction, categoryFilter, page, limit]);
    useEffect(() => { fetch(); }, [fetch]);
    useEffect(() => { setPage(1); }, [search, statusFilter, direction, categoryFilter]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const r = await axios.post(`${API}/api/accounting/imports/bank-csv/stage`, fd, { headers: headers() });
            const d = r.data;
            toast.success(`${d.stats.total} satir staging'e alindi (${d.stats.flagged + d.stats.error} suphe/hata)`, { duration: 5000 });
            navigate(`/accounting/imports/${d.batch_id}`);
        } catch (e) { toast.error(e.response?.data?.detail || 'Import hatasi'); }
        finally { setUploading(false); e.target.value = ''; }
    };

    const handleCreditCardUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.match(/\.(xlsx|xlsm)$/i)) {
            toast.error('Sadece .xlsx dosyalari kabul edilir');
            e.target.value = '';
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const r = await axios.post(
                `${API}/api/accounting/bank-transactions/import-credit-card-xlsx`,
                fd, { headers: headers(), timeout: 120000 }
            );
            const d = r.data;
            const parts = [`${d.imported} kredi karti islemi eklendi`];
            if (d.duplicates > 0) parts.push(`${d.duplicates} duplicate atlandi`);
            if (d.cards_detected?.length) parts.push(`Kartlar: ${d.cards_detected.join(', ')}`);
            if (d.error_count > 0) parts.push(`${d.error_count} hata`);
            toast.success(parts.join(' · '), { duration: 8000 });
            fetch();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(`Kredi karti import hatasi: ${typeof detail === 'string' ? detail : (detail?.message || err.message)}`, { duration: 8000 });
        }
        finally { setUploading(false); e.target.value = ''; }
    };

    // ─── Banka Excel Export / Import ───────────────────────────────────────
    const [bankExcelLoading, setBankExcelLoading] = useState(false);
    const handleBankExcelExport = async () => {
        setBankExcelLoading(true);
        try {
            const r = await axios.get(`${API}/api/accounting/bank-transactions/export.xlsx`, {
                headers: headers(), responseType: 'blob', timeout: 120000,
            });
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url;
            const now = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '_');
            a.download = `flowbil_bank_transactions_${now}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            const total = r.headers['x-total-rows'] || '?';
            toast.success(`${total} hareket indirildi`);
        } catch (e) { toast.error(`Export hatasi: ${e.response?.data?.detail || e.message}`); }
        finally { setBankExcelLoading(false); }
    };
    const handleBankExcelTemplate = async () => {
        try {
            const r = await axios.get(`${API}/api/accounting/bank-transactions/import-template.xlsx`, {
                headers: headers(), responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'flowbil_bank_import_template.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (_e) { toast.error('Sablon indirilemedi'); }
    };
    const handleBankExcelImport = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!file.name.match(/\.(xlsx|xlsm)$/i)) {
            toast.error('Sadece .xlsx dosyalari kabul edilir');
            return;
        }
        if (!window.confirm(`"${file.name}" icindeki hareketleri Import Onizleme'ye al?\n\nVeriler DB'ye DOGRUDAN yazilmaz - once anomaliler (duplicate, cift odeme, bilinmeyen firma vb.) listelenir, sen karar verirsin.`)) return;
        setBankExcelLoading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await axios.post(`${API}/api/accounting/imports/bank/stage`, fd, {
                headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
                timeout: 180000,
            });
            const d = r.data;
            toast.success(`${d.stats.total} satir staging'e alindi (${d.stats.flagged + d.stats.error} suphe/hata)`, { duration: 5000 });
            navigate(`/accounting/imports/${d.batch_id}`);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(`Import hatasi: ${typeof detail === 'string' ? detail : (detail?.message || err.message)}`, { duration: 8000 });
        } finally { setBankExcelLoading(false); }
    };

    const handleAiCategorize = async () => {
        setAiCatRunning(true);
        try {
            const r = await axios.post(`${API}/api/accounting/bank-transactions/categorize-ai`, {}, { headers: headers() });
            toast.success(`${r.data.suggested}/${r.data.processed} kategorize onerildi (kalan: ${r.data.remaining})`);
            fetch();
        } catch (e) { toast.error(e.response?.data?.detail || 'AI hatasi'); }
        finally { setAiCatRunning(false); }
    };

    const handleApplyCategory = async (txId, cat, note) => {
        try {
            await axios.patch(`${API}/api/accounting/bank-transactions/${txId}/category`, { category: cat, note: note || '' }, { headers: headers() });
            toast.success('Kategori atandi');
            setCatEditTx(null);
            fetch();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    const handleRejectAiCategory = async (txId) => {
        try {
            await axios.post(`${API}/api/accounting/bank-transactions/${txId}/category/reject`, {}, { headers: headers() });
            fetch();
        } catch { /* silent */ }
    };

    const toggleSelect = (txId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(txId)) next.delete(txId); else next.add(txId);
            return next;
        });
    };
    const toggleSelectAll = () => {
        if (selectedIds.size === items.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(items.map(t => t.id)));
    };
    const handleBulkCategory = async () => {
        if (!bulkCat || selectedIds.size === 0) return;
        if (!window.confirm(`${selectedIds.size} hareket icin '${CAT_LABELS[bulkCat] || bulkCat}' kategorisi atansin mi?`)) return;
        setBulkRunning(true);
        try {
            const r = await axios.post(`${API}/api/accounting/bank-transactions/bulk-category`,
                { tx_ids: Array.from(selectedIds), category: bulkCat }, { headers: headers() });
            toast.success(`${r.data.updated} hareket guncellendi`);
            setSelectedIds(new Set()); setBulkCat('');
            fetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Bulk kategori hatasi');
        } finally { setBulkRunning(false); }
    };

    const handleUnlink = async (txId) => {
        if (!window.confirm('Bu eslesmeyi cikar?\n\nFatura "Acik" durumuna doner, transfer yeniden "Eslesmeyen" olur.')) return;
        setUnlinkingId(txId);
        try {
            await axios.post(`${API}/api/accounting/reconciliation/unlink`, { transaction_id: txId }, { headers: headers() });
            toast.success('Eslesme cikarildi');
            fetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Cikarma hatasi');
        } finally { setUnlinkingId(null); }
    };

    const handleOpenCounterparty = async (name, iban) => {
        if (resolvingCompany) return;
        if (!name && !iban) return;
        setResolvingCompany(true);
        try {
            let list = [];
            // 1) IBAN ile dene (en kesin)
            if (iban) {
                const r = await axios.get(`${API}/api/accounting/companies?q=${encodeURIComponent(iban)}&limit=5`, { headers: headers() });
                list = r.data.items || [];
            }
            // 2) IBAN bulunmadiysa isim ile dene
            if (list.length === 0 && name) {
                const r2 = await axios.get(`${API}/api/accounting/companies?q=${encodeURIComponent(name)}&limit=5`, { headers: headers() });
                list = r2.data.items || [];
            }
            if (list.length === 0) { toast.info('Bu firma henuz kayitli degil'); }
            else { navigate(`/accounting/companies/${list[0].id}`); }
        } catch { toast.error('Firma aranamadi'); }
        finally { setResolvingCompany(false); }
    };

    const statusColorsTx = { matched: 'bg-emerald-50 text-emerald-700', partially_matched: 'bg-amber-50 text-amber-700', unmatched: 'bg-gray-100 text-gray-500', duplicate: 'bg-red-50 text-red-500', ignored: 'bg-gray-50 text-gray-400' };
    const statusLabelsTx = { matched: 'Eslesmis', partially_matched: 'Kismi', unmatched: 'Eslesmeyen', duplicate: 'Kopya', ignored: 'Yoksay' };

    return (
        <div className="space-y-3" data-testid="acc-bank">
            {/* Direction sub-tabs */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-1 bg-[#F3F4F6] rounded-xl p-1 w-fit">
                    <button onClick={() => setDirection('')} className={`px-4 py-1.5 rounded-lg text-xs font-semibold ${direction === '' ? 'bg-white text-[#111] shadow-sm' : 'text-[#6B7280]'}`} data-testid="acc-bank-dir-all">Tumu</button>
                    <button onClick={() => setDirection('in')} className={`px-4 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${direction === 'in' ? 'bg-white text-emerald-600 shadow-sm' : 'text-[#6B7280]'}`} data-testid="acc-bank-dir-in"><ArrowDownLeft size={12} />Gelen</button>
                    <button onClick={() => setDirection('out')} className={`px-4 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${direction === 'out' ? 'bg-white text-red-500 shadow-sm' : 'text-[#6B7280]'}`} data-testid="acc-bank-dir-out"><ArrowUpRight size={12} />Giden</button>
                </div>
                <button onClick={handleAiCategorize} disabled={aiCatRunning}
                    className="h-8 px-3 bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white text-xs font-semibold rounded-lg flex items-center gap-1 disabled:opacity-50"
                    data-testid="acc-bank-ai-categorize-btn"
                    title="Faturasiz hareketleri AI ile kategorize et (oneri sadece, sen onaylarsin)">
                    <Sparkles size={12} className={aiCatRunning ? 'animate-pulse' : ''} />
                    {aiCatRunning ? 'AI calisiyor...' : 'AI Kategorize Et'}
                </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Firma, referans ara..."
                        className="w-full h-9 pl-9 pr-3 bg-white border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]" data-testid="acc-bank-search" />
                </div>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 px-3 bg-white border border-[#E5E7EB] rounded-xl text-sm" data-testid="acc-bank-status-filter">
                    <option value="">Tum Durumlar</option>
                    <option value="unmatched">Eslesmeyen</option>
                    <option value="matched">Eslesmis</option>
                </select>
                <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="h-9 px-3 bg-white border border-[#E5E7EB] rounded-xl text-sm" data-testid="acc-bank-cat-filter">
                    <option value="">Tum Kategoriler</option>
                    <option value="uncategorized">Kategorisiz</option>
                    <option value="ai_suggested">AI Onerisi Var</option>
                    <option disabled>──────────</option>
                    {Object.entries(CAT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <label className={`h-9 px-4 bg-[#111827] text-white text-sm font-medium rounded-xl flex items-center gap-1.5 cursor-pointer ${uploading ? 'opacity-50' : ''}`} data-testid="acc-bank-upload-btn">
                    <Upload size={13} />{uploading ? 'Yukleniyor...' : 'CSV Yukle'}
                    <input type="file" accept=".csv" onChange={handleUpload} className="hidden" disabled={uploading} />
                </label>
                <label className={`h-9 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium rounded-xl flex items-center gap-1.5 cursor-pointer ${uploading ? 'opacity-50' : ''}`}
                    data-testid="acc-bank-cc-upload-btn"
                    title="Kredi karti harcamalari Excel dosyasi (ayni format: Tarih, Tuccar, Sehir, Kategori, Tutar, Kart No)">
                    <Upload size={13} />Kredi Karti Excel
                    <input type="file" accept=".xlsx,.xlsm" onChange={handleCreditCardUpload} className="hidden" disabled={uploading} data-testid="acc-bank-cc-upload-input" />
                </label>

                {/* Excel Export / Import */}
                <div className="flex items-center gap-1 pl-2 border-l border-[#E5E7EB]">
                    <button
                        onClick={handleBankExcelExport}
                        disabled={bankExcelLoading}
                        className="h-9 px-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 hover:bg-emerald-100 disabled:opacity-50"
                        data-testid="acc-bank-excel-export-btn"
                        title="Tum banka hareketlerini Excel olarak indir"
                    >
                        <FileDown size={11} />Excel Export
                    </button>
                    <label
                        className={`h-9 px-3 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 cursor-pointer hover:bg-blue-100 ${bankExcelLoading ? 'opacity-50 pointer-events-none' : ''}`}
                        data-testid="acc-bank-excel-import-btn"
                        title="Excel'den toplu hareket import et (duplicate kontrolu otomatik)"
                    >
                        <Upload size={11} />Excel Import
                        <input type="file" accept=".xlsx,.xlsm" onChange={handleBankExcelImport} className="hidden" disabled={bankExcelLoading} />
                    </label>
                    <button
                        onClick={handleBankExcelTemplate}
                        className="h-9 px-2 bg-white border border-[#E5E7EB] text-[#6B7280] text-xs font-semibold rounded-xl flex items-center gap-1 hover:bg-[#F3F4F6]"
                        data-testid="acc-bank-excel-template-btn"
                        title="Bos Excel sablonu indir"
                    >
                        Sablon
                    </button>
                </div>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                {selectedIds.size > 0 && (
                    <div className="bg-[#111827] text-white px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="acc-bank-bulk-bar">
                        <span className="text-xs font-semibold"><strong>{selectedIds.size}</strong> hareket secili</span>
                        <select value={bulkCat} onChange={e => setBulkCat(e.target.value)}
                            className="h-7 px-2 bg-white/10 text-white text-xs rounded-md border border-white/20" data-testid="acc-bank-bulk-cat-select">
                            <option value="" className="text-[#111]">Kategori sec...</option>
                            {Object.entries(CAT_LABELS).map(([k, l]) => <option key={k} value={k} className="text-[#111]">{l}</option>)}
                        </select>
                        <button onClick={handleBulkCategory} disabled={!bulkCat || bulkRunning}
                            className="h-7 px-3 bg-emerald-500 text-white text-xs font-bold rounded-md disabled:opacity-50"
                            data-testid="acc-bank-bulk-apply-btn">
                            {bulkRunning ? '...' : 'Uygula'}
                        </button>
                        <button onClick={() => setSelectedIds(new Set())} className="h-7 px-3 bg-white/10 text-white text-xs rounded-md">Sec iptal</button>
                    </div>
                )}
                {loading ? <div className="p-8 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div> :
                    items.length === 0 ? <div className="p-8 text-center text-sm text-[#9CA3AF]">Banka islemi bulunamadi</div> : (
                        <>
                            <div className="hidden md:grid grid-cols-[28px_90px_220px_1fr_140px_100px_90px_100px] px-4 py-2 border-b border-[#F3F4F6] bg-[#FAFAFA] gap-3">
                                <input type="checkbox" checked={selectedIds.size === items.length && items.length > 0} onChange={toggleSelectAll}
                                    className="w-3.5 h-3.5 accent-[#111827]" data-testid="acc-bank-select-all-cb" />
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase">Tarih</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase">Karsi Taraf</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase">Aciklama / Iletisim</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase">Kategori</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase text-right">Tutar</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase text-center">Durum</span>
                                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase text-center">Islem</span>
                            </div>
                            <div className="divide-y divide-[#F3F4F6]">
                                {items.map(t => (
                                    <BankTxRow key={t.id} tx={t}
                                        statusColors={statusColorsTx} statusLabels={statusLabelsTx}
                                        onCounterpartyClick={handleOpenCounterparty}
                                        onEditCategory={() => setCatEditTx(t)}
                                        onApproveAiCat={() => handleApplyCategory(t.id, t.ai_suggested_category)}
                                        onRejectAiCat={() => handleRejectAiCategory(t.id)}
                                        selected={selectedIds.has(t.id)}
                                        onToggleSelect={() => toggleSelect(t.id)}
                                        onUnlink={() => handleUnlink(t.id)}
                                        unlinking={unlinkingId === t.id} />
                                ))}
                            </div>
                            <Pagination page={page} total={total} limit={limit} onPageChange={setPage} testId="acc-bank-pagination" />
                        </>
                    )}
            </div>
            {catEditTx && <CategoryEditModal tx={catEditTx} onClose={() => setCatEditTx(null)} onSave={(cat, note) => handleApplyCategory(catEditTx.id, cat, note)} />}
            {companyDetailId && <CompanyDetailModal companyId={companyDetailId} onClose={() => setCompanyDetailId(null)} />}
        </div>
    );
}

function BankTxRow({ tx, statusColors, statusLabels, onCounterpartyClick, onEditCategory, onApproveAiCat, onRejectAiCat, selected, onToggleSelect, onUnlink, unlinking }) {
    return (
        <div className="md:grid md:grid-cols-[28px_90px_220px_1fr_140px_100px_90px_100px] flex flex-col gap-1 md:gap-3 px-4 py-3 hover:bg-[#F9FAFB] md:items-center" data-testid={`acc-tx-${tx.id}`}>
            <input type="checkbox" checked={!!selected} onChange={onToggleSelect}
                className="w-3.5 h-3.5 accent-[#111827] mt-1 md:mt-0" data-testid={`acc-tx-select-${tx.id}`}
                onClick={e => e.stopPropagation()} />
            <div className="flex flex-col">
                <span className="text-xs text-[#6B7280]">{tx.transaction_date}</span>
                {tx.value_date && tx.value_date !== tx.transaction_date && <span className="text-[10px] text-[#9CA3AF]">val. {tx.value_date}</span>}
            </div>
            <div className="min-w-0">
                <button onClick={() => onCounterpartyClick(tx.counterparty_name, tx.counterparty_iban)}
                    disabled={!tx.counterparty_name}
                    className="text-sm font-medium text-[#111] hover:text-[#007AFF] hover:underline text-left truncate block w-full disabled:hover:no-underline disabled:cursor-default"
                    data-testid={`acc-tx-counterparty-${tx.id}`}
                    title={tx.counterparty_name ? 'Cari ekstreyi ac' : ''}>
                    {tx.counterparty_name || '—'}
                </button>
                {tx.counterparty_iban && <span className="text-[10px] text-[#9CA3AF] block truncate">{tx.counterparty_iban}</span>}
            </div>
            <div className="min-w-0 break-words text-[12px] text-[#374151] whitespace-pre-wrap">
                {tx.communication || '—'}
                {tx.reference && tx.reference !== 'NOTPROVIDED' && (
                    <div className="text-[10px] text-[#9CA3AF] mt-0.5">Ref: {tx.reference}</div>
                )}
                {tx.bank_ref && <div className="text-[10px] text-[#9CA3AF]">Bank: {tx.bank_ref}</div>}
            </div>
            <div className="flex flex-col gap-0.5 min-w-0">
                {tx.category ? (
                    <button onClick={onEditCategory}
                        className={`text-[10px] px-2 py-0.5 rounded-md font-semibold w-fit ${CAT_COLORS[tx.category] || 'bg-gray-100 text-gray-600'} hover:opacity-80`}
                        data-testid={`acc-tx-cat-${tx.id}`}
                        title="Kategoriyi degistir">
                        {CAT_LABELS[tx.category] || tx.category} <Edit size={9} className="inline ml-0.5" />
                    </button>
                ) : tx.ai_suggested_category ? (
                    <div className="flex flex-col gap-0.5">
                        <span className={`text-[10px] px-2 py-0.5 rounded-md font-semibold w-fit border border-dashed ${CAT_COLORS[tx.ai_suggested_category] || 'bg-gray-100 text-gray-600'}`}
                            data-testid={`acc-tx-aicat-${tx.id}`}
                            title={tx.ai_category_reason || ''}>
                            <Sparkles size={9} className="inline mr-0.5" />AI: {CAT_LABELS[tx.ai_suggested_category] || tx.ai_suggested_category}
                        </span>
                        <div className="flex gap-1">
                            <button onClick={onApproveAiCat} className="text-[9px] px-1.5 py-0.5 bg-emerald-600 text-white rounded font-semibold" data-testid={`acc-tx-aicat-approve-${tx.id}`}>Onayla</button>
                            <button onClick={onEditCategory} className="text-[9px] px-1.5 py-0.5 bg-blue-600 text-white rounded font-semibold" data-testid={`acc-tx-aicat-edit-${tx.id}`}>Degistir</button>
                            <button onClick={onRejectAiCat} className="text-[9px] px-1.5 py-0.5 bg-white border border-red-200 text-red-500 rounded font-semibold" data-testid={`acc-tx-aicat-reject-${tx.id}`}>Reddet</button>
                        </div>
                    </div>
                ) : (
                    <button onClick={onEditCategory}
                        className="text-[10px] text-[#9CA3AF] hover:text-[#007AFF] underline w-fit"
                        data-testid={`acc-tx-cat-add-${tx.id}`}>
                        + Kategori
                    </button>
                )}
            </div>
            <span className={`text-sm font-bold text-right ${tx.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{tx.amount >= 0 ? '+' : ''}{fmtMoney(tx.amount)}</span>
            <div className="text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[tx.status] || 'bg-gray-100 text-gray-500'}`}>{statusLabels[tx.status] || tx.status}</span></div>
            <div className="text-center">
                {tx.status === 'matched' && onUnlink ? (
                    <button onClick={onUnlink} disabled={unlinking}
                        className="text-[10px] px-2 py-1 bg-white border border-red-200 text-red-500 hover:bg-red-50 rounded-md font-semibold flex items-center gap-1 mx-auto disabled:opacity-50"
                        data-testid={`acc-tx-unlink-${tx.id}`}
                        title="Bu eslesmeyi cikar (faturaya geri ac)">
                        {unlinking ? <RefreshCw size={9} className="animate-spin" /> : <X size={9} />}
                        Cikar
                    </button>
                ) : <span className="text-[10px] text-[#9CA3AF]">—</span>}
            </div>
        </div>
    );
}

function CategoryEditModal({ tx, onClose, onSave }) {
    const [cat, setCat] = useState(tx.category || tx.ai_suggested_category || '');
    const [customCat, setCustomCat] = useState('');
    const [note, setNote] = useState(tx.category_note || '');
    const [saving, setSaving] = useState(false);
    const finalCat = customCat.trim() || cat;
    const submit = async (e) => {
        e.preventDefault();
        if (!finalCat) return toast.error('Kategori sec veya yaz');
        setSaving(true);
        try { await onSave(finalCat, note); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3" data-testid="acc-cat-edit-modal">
                <div className="flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#111]">Kategori Ata</h3>
                    <button type="button" onClick={onClose} className="text-[#9CA3AF] hover:text-[#111]"><X size={18} /></button>
                </div>
                <div className="bg-[#F9FAFB] rounded-xl p-3 text-xs space-y-1">
                    <div className="flex justify-between"><span className="text-[#9CA3AF]">Tarih</span><span>{tx.transaction_date}</span></div>
                    <div className="flex justify-between"><span className="text-[#9CA3AF]">Karsi Taraf</span><span className="font-semibold text-right">{tx.counterparty_name || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-[#9CA3AF]">Tutar</span><span className={`font-bold ${tx.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{tx.amount >= 0 ? '+' : ''}{fmtMoney(tx.amount)} EUR</span></div>
                    {tx.communication && <div><span className="text-[#9CA3AF]">Aciklama:</span><div className="text-[#374151] whitespace-pre-wrap break-words mt-0.5">{tx.communication}</div></div>}
                    {tx.ai_category_reason && (
                        <div className="mt-2 pt-2 border-t border-[#E5E7EB]">
                            <span className="text-[#8B5CF6] font-semibold text-[11px]"><Sparkles size={10} className="inline mr-0.5" />AI: {CAT_LABELS[tx.ai_suggested_category] || tx.ai_suggested_category}</span>
                            <div className="text-[#6B7280] text-[11px] mt-0.5">{tx.ai_category_reason}</div>
                        </div>
                    )}
                </div>
                <div>
                    <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Sistem Kategorileri</label>
                    <div className="grid grid-cols-3 gap-1.5 mt-1">
                        {Object.entries(CAT_LABELS).map(([k, l]) => (
                            <button key={k} type="button" onClick={() => { setCat(k); setCustomCat(''); }}
                                className={`text-[11px] px-2 py-1.5 rounded-lg font-semibold ${(cat === k && !customCat) ? 'bg-[#111827] text-white' : `${CAT_COLORS[k]} hover:opacity-80`}`}
                                data-testid={`acc-cat-opt-${k}`}>{l}</button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Veya Ozel Kategori Yaz</label>
                    <input value={customCat} onChange={e => setCustomCat(e.target.value)}
                        placeholder="Ornek: Yakit, Donanim, ..."
                        className="w-full h-9 px-3 mt-1 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]"
                        data-testid="acc-cat-custom-input" />
                </div>
                <div>
                    <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Not (opsiyonel)</label>
                    <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                        className="w-full px-3 py-2 mt-1 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#007AFF]"
                        data-testid="acc-cat-note-input" />
                </div>
                <button type="submit" disabled={saving || !finalCat} className="w-full h-10 bg-[#111827] text-white text-sm font-semibold rounded-xl disabled:opacity-50" data-testid="acc-cat-save-btn">
                    {saving ? 'Kaydediliyor...' : `Kaydet${finalCat ? ` (${CAT_LABELS[finalCat] || finalCat})` : ''}`}
                </button>
            </form>
        </div>
    );
}

// ─── RECONCILIATION ───
function AccReconciliation() {
    const [unmatched, setUnmatched] = useState([]);
    const [openInv, setOpenInv] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [autoRunning, setAutoRunning] = useState(false);
    const [aiRunning, setAiRunning] = useState(false);
    const [aiProgress, setAiProgress] = useState(null);
    const [bulkRunning, setBulkRunning] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [quickApprove, setQuickApprove] = useState(() => {
        try { return localStorage.getItem('acc_quick_approve') === '1'; } catch { return false; }
    });
    const toggleQuickApprove = () => {
        setQuickApprove(prev => {
            const next = !prev;
            try { localStorage.setItem('acc_quick_approve', next ? '1' : '0'); } catch {}
            return next;
        });
    };
    const [selectedTx, setSelectedTx] = useState(null);
    const [selectedInv, setSelectedInv] = useState(null);
    const [view, setView] = useState('suggestions');
    const [direction, setDirection] = useState('');   // '' | 'in' | 'out'

    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const [txRes, invRes] = await Promise.all([
                axios.get(`${API}/api/accounting/bank-transactions?status=unmatched&limit=2000${direction ? `&direction=${direction}` : ''}`, { headers: headers() }),
                axios.get(`${API}/api/accounting/invoices?status=open&limit=2000${direction === 'in' ? '&type=sales' : direction === 'out' ? '&type=purchase' : ''}`, { headers: headers() }),
            ]);
            setUnmatched(txRes.data.items || []);
            setOpenInv(invRes.data.items || []);
        } catch { toast.error('Yuklenemedi'); }
        finally { setLoading(false); }
    }, [direction]);

    const fetchSuggestions = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/accounting/reconciliation/suggestions?limit=2000${direction ? `&direction=${direction}` : ''}`, { headers: headers() });
            setSuggestions(r.data.items || []);
        } catch { /* silent */ }
    }, [direction]);

    useEffect(() => { fetch(); fetchSuggestions(); }, [fetch, fetchSuggestions]);

    const handleAutoReconcile = async () => {
        setAutoRunning(true);
        try {
            const r = await axios.post(`${API}/api/accounting/reconciliation/auto`, {}, { headers: headers() });
            toast.success(`${r.data.auto_matched} otomatik eslesti, ${r.data.pending_suggestions} oneri kuyruga eklendi`);
            fetch();
            fetchSuggestions();
        } catch { toast.error('Hata'); }
        finally { setAutoRunning(false); }
    };

    const handleRunAi = async () => {
        setAiRunning(true);
        try {
            const r = await axios.post(`${API}/api/accounting/reconciliation/run-ai`, {}, { headers: headers() });
            if (r.data.status === 'already_running') {
                toast.info('AI zaten calisiyor, ilerleme gosteriliyor');
            } else {
                toast.success(`AI baslatildi (${r.data.total} oneri arkaplanda isleniyor)`);
            }
            // Start polling
            const poll = async () => {
                try {
                    const s = await axios.get(`${API}/api/accounting/reconciliation/run-ai/status`, { headers: headers() });
                    const job = s.data || {};
                    if (job.status === 'running') {
                        const total = job.total || 1;
                        const processed = job.processed || 0;
                        const pct = Math.min(100, Math.round((processed / total) * 100));
                        setAiProgress({ pct, processed, total, approved: job.auto_matched || 0 });
                        setTimeout(poll, 2500);
                    } else {
                        setAiProgress(null);
                        setAiRunning(false);
                        if (job.status === 'completed') {
                            toast.success(`AI tamamlandi: ${job.auto_matched || 0} otomatik onayli`);
                        } else if (job.status === 'failed') {
                            toast.error(`AI hatasi: ${job.error || 'bilinmeyen'}`);
                        }
                        fetchSuggestions(); fetch();
                    }
                } catch (e) {
                    setAiRunning(false); setAiProgress(null);
                }
            };
            setTimeout(poll, 1500);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'AI baslatilamadi');
            setAiRunning(false);
        }
    };

    const handleCancelAi = async () => {
        try {
            const r = await axios.post(`${API}/api/accounting/reconciliation/run-ai/cancel`, {}, { headers: headers() });
            if (r.data.cancelled > 0) {
                toast.success('AI durduruldu (devam eden batch tamamlandiktan sonra durur)');
            } else {
                toast.info('Calisan AI yok');
                setAiRunning(false); setAiProgress(null);
            }
        } catch { toast.error('Iptal edilemedi'); }
    };

    const [anomalies, setAnomalies] = useState(null);
    const [anomaliesLoading, setAnomaliesLoading] = useState(false);
    const handleFindAnomalies = async () => {
        setAnomaliesLoading(true);
        try {
            const r = await axios.get(`${API}/api/accounting/reconciliation/anomalies`, { headers: headers() });
            setAnomalies(r.data);
            if (r.data.total === 0) toast.success('Anomali bulunamadi - tum eslesmeler tutarli!');
            else toast.warning(`${r.data.total} fatura icin ${fmtMoney(r.data.total_over_amount)} EUR fazla eslesme tespit edildi`, { duration: 7000 });
        } catch { toast.error('Anomali bulunamadi'); }
        finally { setAnomaliesLoading(false); }
    };
    const handleCleanupAnomaly = async (invId, label) => {
        if (!window.confirm(`'${label}' faturasindan FAZLA eslesmeleri otomatik cikar?\n\nFIFO: fatura kapanana kadar tut, gerisini cikar.`)) return;
        try {
            const r = await axios.post(`${API}/api/accounting/reconciliation/cleanup-anomaly`,
                { invoice_id: invId, keep_strategy: 'first_n' }, { headers: headers() });
            toast.success(`${r.data.unlinked} fazla transfer cikarildi, ${r.data.kept} korundu`);
            handleFindAnomalies();
            fetch();
        } catch (e) { toast.error(e.response?.data?.detail || 'Temizleme hatasi'); }
    };

    const handleSmartSelect = () => {
        // Yuksek guvenli onerileri sec: skor 100 OR (skor>=80 + AI guven >=85)
        const safe = suggestions.filter(s =>
            (s.top_rule_score >= 100) ||
            (s.top_rule_score >= 80 && (s.ai_confidence || 0) >= 85)
        ).map(s => s.id);
        setSelectedIds(new Set(safe));
        toast.success(`${safe.length} guvenli oneri secildi`);
    };

    const handleBulkApprove = async () => {
        if (selectedIds.size === 0) return toast.error('Once oneri secin');
        if (!quickApprove && !window.confirm(`${selectedIds.size} oneri toplu onaylansin mi?`)) return;
        setBulkRunning(true);
        try {
            const r = await axios.post(`${API}/api/accounting/reconciliation/suggestions/bulk-approve`,
                { suggestion_ids: Array.from(selectedIds) }, { headers: headers() });
            toast.success(`${r.data.approved} onaylandi (${r.data.multi_approved} multi), ${r.data.failed} hata`);
            setSelectedIds(new Set());
            fetchSuggestions(); fetch();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setBulkRunning(false); }
    };

    const handleBulkReject = async () => {
        if (selectedIds.size === 0) return toast.error('Once oneri secin');
        if (!quickApprove && !window.confirm(`${selectedIds.size} oneri toplu reddedilsin mi?`)) return;
        setBulkRunning(true);
        try {
            const r = await axios.post(`${API}/api/accounting/reconciliation/suggestions/bulk-reject`,
                { suggestion_ids: Array.from(selectedIds) }, { headers: headers() });
            toast.success(`${r.data.rejected} reddedildi`);
            setSelectedIds(new Set());
            fetchSuggestions();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setBulkRunning(false); }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id); else s.add(id);
            return s;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === suggestions.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(suggestions.map(s => s.id)));
    };

    const handleManualMatch = async () => {
        if (!selectedTx || !selectedInv) return toast.error('Islem ve fatura secin');
        try {
            await axios.post(`${API}/api/accounting/reconciliation/manual`, { transaction_id: selectedTx.id, invoice_id: selectedInv.id }, { headers: headers() });
            toast.success('Eslestirme yapildi');
            setSelectedTx(null); setSelectedInv(null); fetch();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    const [processingId, setProcessingId] = useState(null);

    const handleApproveSuggestion = async (sug, invoiceId, isMulti) => {
        if (isMulti) {
            // SuggestionCard already called the API; remove optimistically + refresh
            setSuggestions(prev => prev.filter(s => s.id !== sug.id));
            fetchSuggestions(); fetch();
            return;
        }
        if (processingId) return;   // double-click koruma
        setProcessingId(sug.id);
        // Optimistik kaldir
        setSuggestions(prev => prev.filter(s => s.id !== sug.id));
        try {
            await axios.post(`${API}/api/accounting/reconciliation/suggestions/${sug.id}/approve`,
                invoiceId ? { invoice_id: invoiceId } : {},
                { headers: headers() });
            toast.success('Onaylandi');
            fetchSuggestions(); fetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
            fetchSuggestions();   // restore
        } finally { setProcessingId(null); }
    };

    const handleRejectSuggestion = async (sug) => {
        if (processingId) return;
        setProcessingId(sug.id);
        setSuggestions(prev => prev.filter(s => s.id !== sug.id));
        try {
            await axios.post(`${API}/api/accounting/reconciliation/suggestions/${sug.id}/reject`, {}, { headers: headers() });
            toast.success('Reddedildi');
            fetchSuggestions();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
            fetchSuggestions();
        } finally { setProcessingId(null); }
    };

    return (
        <div className="space-y-3" data-testid="acc-reconciliation">
            <div className="flex flex-wrap gap-2">
                <button onClick={handleAutoReconcile} disabled={autoRunning}
                    className="h-9 px-4 bg-[#059669] text-white text-sm font-medium rounded-xl flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="acc-auto-reconcile-btn">
                    <RefreshCw size={13} className={autoRunning ? 'animate-spin' : ''} />Kural ile Eslestir
                </button>
                <button onClick={handleRunAi} disabled={aiRunning}
                    className="h-9 px-4 bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white text-sm font-medium rounded-xl flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="acc-ai-run-btn"
                    title="Belirsiz oneriler icin Claude Haiku 4.5'a sor (50'lik batch)">
                    <Sparkles size={13} className={aiRunning ? 'animate-pulse' : ''} />AI ile Coz
                </button>
                <button onClick={handleManualMatch} disabled={!selectedTx || !selectedInv}
                    className="h-9 px-4 bg-[#007AFF] text-white text-sm font-medium rounded-xl flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="acc-manual-match-btn">
                    <Link2 size={13} />Manuel Eslestir
                </button>
                <button onClick={handleFindAnomalies} disabled={anomaliesLoading}
                    className="h-9 px-4 bg-orange-600 text-white text-sm font-medium rounded-xl flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="acc-anomaly-btn"
                    title="Faturaya bagli transferlerin toplami fatura tutarini asanlari bul (yanlis eslesme tespiti)">
                    <AlertTriangle size={13} className={anomaliesLoading ? 'animate-spin' : ''} />Anomali Bul
                </button>
                <div className="ml-auto flex gap-1 bg-[#F3F4F6] rounded-xl p-1">
                    <button onClick={() => setView('manual')} className={`px-3 py-1 rounded-lg text-xs font-semibold ${view === 'manual' ? 'bg-white text-[#111] shadow-sm' : 'text-[#6B7280]'}`} data-testid="acc-view-manual">Manuel</button>
                    <button onClick={() => setView('suggestions')} className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 ${view === 'suggestions' ? 'bg-white text-[#111] shadow-sm' : 'text-[#6B7280]'}`} data-testid="acc-view-suggestions">
                        <Sparkles size={11} />Oneriler {suggestions.length > 0 && <span className="bg-[#8B5CF6] text-white text-[9px] rounded-full px-1.5 py-0.5">{suggestions.length}</span>}
                    </button>
                </div>
            </div>

            {/* Direction sub-tabs (Tumu / Gelen=Satis / Giden=Alis) */}
            <div className="flex gap-1 bg-[#F3F4F6] rounded-xl p-1 w-fit">
                <button onClick={() => setDirection('')} className={`px-4 py-1.5 rounded-lg text-xs font-semibold ${direction === '' ? 'bg-white text-[#111] shadow-sm' : 'text-[#6B7280]'}`} data-testid="acc-recon-dir-all">Tumu</button>
                <button onClick={() => setDirection('in')} className={`px-4 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${direction === 'in' ? 'bg-white text-emerald-600 shadow-sm' : 'text-[#6B7280]'}`} data-testid="acc-recon-dir-in"><ArrowDownLeft size={12} />Gelen Para (Satis)</button>
                <button onClick={() => setDirection('out')} className={`px-4 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${direction === 'out' ? 'bg-white text-red-500 shadow-sm' : 'text-[#6B7280]'}`} data-testid="acc-recon-dir-out"><ArrowUpRight size={12} />Giden Para (Alis)</button>
            </div>

            {anomalies && anomalies.total > 0 && (
                <div className="bg-orange-50 border border-orange-300 rounded-xl p-4" data-testid="acc-anomaly-panel">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-bold text-orange-900 flex items-center gap-1.5">
                            <AlertTriangle size={14} />{anomalies.total} fatura icin fazla eslesme tespit edildi · {fmtMoney(anomalies.total_over_amount)} EUR fazla
                        </h3>
                        <button onClick={() => setAnomalies(null)} className="text-orange-700 hover:text-orange-900"><X size={14} /></button>
                    </div>
                    <p className="text-[11px] text-orange-800 mb-3">Bir faturaya bagli transferlerin TOPLAMI fatura tutarindan fazla - yani kullanici yanlislikla, eski bug nedeniyle birden fazla transfer ayni faturaya bagli. "Otomatik Temizle" FIFO yapar (fatura kapanana kadar tutar, gerisini cikarir).</p>
                    <div className="bg-white rounded-lg overflow-hidden">
                        <table className="w-full text-xs">
                            <thead className="bg-orange-100 text-orange-900 text-[10px] uppercase">
                                <tr>
                                    <th className="text-left px-3 py-2">Fatura</th>
                                    <th className="text-left px-3 py-2">Firma</th>
                                    <th className="text-right px-3 py-2">Tutar</th>
                                    <th className="text-right px-3 py-2">TX</th>
                                    <th className="text-right px-3 py-2">TX Toplam</th>
                                    <th className="text-right px-3 py-2">Fazla</th>
                                    <th className="text-right px-3 py-2">Aksiyon</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-orange-100">
                                {anomalies.anomalies.map(a => (
                                    <tr key={a.invoice_id} className="hover:bg-orange-50/50" data-testid={`acc-anomaly-row-${a.invoice_id}`}>
                                        <td className="px-3 py-2 font-semibold text-[#111] max-w-[160px] truncate">{a.invoice_number}</td>
                                        <td className="px-3 py-2 text-[#374151] max-w-[180px] truncate">{a.contact_name}</td>
                                        <td className="px-3 py-2 text-right text-[#111] font-medium">{fmtMoney(a.invoice_total)}</td>
                                        <td className="px-3 py-2 text-right text-orange-700 font-bold">{a.tx_count}x</td>
                                        <td className="px-3 py-2 text-right text-red-600 font-bold">{fmtMoney(a.tx_total)}</td>
                                        <td className="px-3 py-2 text-right text-red-700 font-bold">+{fmtMoney(a.over_amount)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <button onClick={() => handleCleanupAnomaly(a.invoice_id, a.invoice_number)}
                                                className="h-7 px-2 bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-semibold rounded-md"
                                                data-testid={`acc-anomaly-cleanup-${a.invoice_id}`}>
                                                Otomatik Temizle
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {loading ? <div className="p-8 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div> : view === 'suggestions' ? (
                <>
                    {/* AI Progress bar */}
                    {aiRunning && aiProgress && (
                        <div className="bg-white border border-[#E5E7EB] rounded-xl p-3" data-testid="acc-ai-progress">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[11px] font-semibold text-[#8B5CF6] flex items-center gap-1">
                                    <Sparkles size={12} className="animate-pulse" /> AI calisiyor: {aiProgress.processed}/{aiProgress.total}
                                </span>
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] text-[#6B7280]">{aiProgress.approved} otomatik onayli</span>
                                    <button onClick={handleCancelAi}
                                        className="h-6 px-2 bg-white hover:bg-red-50 border border-red-200 text-red-500 text-[10px] font-semibold rounded-md flex items-center gap-1"
                                        data-testid="acc-ai-cancel-btn"
                                        title="AI'yi durdur (devam eden chunk bitince durur)">
                                        <X size={11} />Durdur
                                    </button>
                                </div>
                            </div>
                            <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] transition-all duration-300" style={{ width: `${aiProgress.pct}%` }} />
                            </div>
                        </div>
                    )}

                    {/* Bulk toolbar */}
                    {suggestions.length > 0 && (
                        <div className="flex items-center gap-2 bg-[#F9FAFB] rounded-xl px-3 py-2" data-testid="acc-bulk-toolbar">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox"
                                    checked={selectedIds.size > 0 && selectedIds.size === suggestions.length}
                                    onChange={toggleSelectAll}
                                    className="w-4 h-4 rounded border-[#D1D5DB]"
                                    data-testid="acc-bulk-selectall" />
                                <span className="text-xs font-medium text-[#6B7280]">
                                    {selectedIds.size > 0 ? `${selectedIds.size} secili` : 'Tumunu sec'}
                                </span>
                            </label>
                            <button onClick={handleSmartSelect}
                                className="h-8 px-3 bg-gradient-to-r from-[#10B981] to-[#3B82F6] hover:opacity-90 text-white text-xs font-semibold rounded-lg flex items-center gap-1"
                                data-testid="acc-smart-select-btn"
                                title="AI guven %85+ veya skor 100 olan onerileri sec">
                                <Sparkles size={12} />Guvenli Olanlari Sec
                            </button>
                            <label className="flex items-center gap-1.5 cursor-pointer ml-2 select-none"
                                data-testid="acc-quick-approve-toggle"
                                title="Acikken toplu onayda/redde onay penceresi gosterilmez">
                                <input type="checkbox"
                                    checked={quickApprove}
                                    onChange={toggleQuickApprove}
                                    className="w-3.5 h-3.5 rounded border-[#D1D5DB] accent-emerald-600"
                                    data-testid="acc-quick-approve-checkbox" />
                                <span className={`text-[11px] font-semibold ${quickApprove ? 'text-emerald-700' : 'text-[#6B7280]'}`}>
                                    Hizli Onay Modu{quickApprove ? ' (Acik)' : ''}
                                </span>
                            </label>
                            {selectedIds.size > 0 && (
                                <>
                                    <button onClick={handleBulkApprove} disabled={bulkRunning}
                                        className="ml-auto h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1 disabled:opacity-50"
                                        data-testid="acc-bulk-approve-btn">
                                        <CheckCircle2 size={12} />Secilenleri Onayla ({selectedIds.size})
                                    </button>
                                    <button onClick={handleBulkReject} disabled={bulkRunning}
                                        className="h-8 px-3 bg-white hover:bg-red-50 border border-[#E5E7EB] text-red-500 text-xs font-medium rounded-lg flex items-center gap-1 disabled:opacity-50"
                                        data-testid="acc-bulk-reject-btn">
                                        <X size={12} />Secilenleri Reddet
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    <SuggestionsPanel
                        suggestions={suggestions}
                        onApprove={handleApproveSuggestion}
                        onReject={handleRejectSuggestion}
                        selectedIds={selectedIds}
                        onToggleSelect={toggleSelect}
                        processingId={processingId}
                    />
                </>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-[#F3F4F6] bg-[#FAFAFA]">
                            <h3 className="text-sm font-semibold text-[#111]">Eslesmeyen Islemler ({unmatched.length})</h3>
                        </div>
                        <div className="max-h-[50vh] overflow-y-auto divide-y divide-[#F3F4F6]">
                            {unmatched.length === 0 ? <div className="p-6 text-center text-xs text-[#9CA3AF]">Tum islemler eslesmis</div> :
                                unmatched.map(t => (
                                    <button key={t.id} onClick={() => setSelectedTx(selectedTx?.id === t.id ? null : t)}
                                        className={`w-full text-left px-4 py-2.5 transition-colors ${selectedTx?.id === t.id ? 'bg-[#007AFF]/10 border-l-2 border-[#007AFF]' : 'hover:bg-[#F9FAFB]'}`}
                                        data-testid={`recon-tx-${t.id}`}>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-[#111] truncate">{t.counterparty_name || '—'}</span>
                                            <span className={`text-sm font-bold ${t.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{t.amount >= 0 ? '+' : ''}{fmtMoney(t.amount)}</span>
                                        </div>
                                        <div className="text-[10px] text-[#9CA3AF] mt-0.5">{t.transaction_date} · {t.communication || t.reference || '—'}</div>
                                    </button>
                                ))}
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-[#F3F4F6] bg-[#FAFAFA]">
                            <h3 className="text-sm font-semibold text-[#111]">Acik Faturalar ({openInv.length})</h3>
                        </div>
                        <div className="max-h-[50vh] overflow-y-auto divide-y divide-[#F3F4F6]">
                            {openInv.length === 0 ? <div className="p-6 text-center text-xs text-[#9CA3AF]">Acik fatura yok</div> :
                                openInv.map(inv => (
                                    <button key={inv.id} onClick={() => setSelectedInv(selectedInv?.id === inv.id ? null : inv)}
                                        className={`w-full text-left px-4 py-2.5 transition-colors ${selectedInv?.id === inv.id ? 'bg-[#007AFF]/10 border-l-2 border-[#007AFF]' : 'hover:bg-[#F9FAFB]'}`}
                                        data-testid={`recon-inv-${inv.id}`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className="text-sm font-medium text-[#111]">{inv.invoice_number}</span>
                                                <span className={`ml-2 text-[10px] ${inv.type === 'sales' ? 'text-emerald-600' : 'text-blue-600'}`}>{inv.type === 'sales' ? 'Satis' : 'Alis'}</span>
                                            </div>
                                            <span className="text-sm font-bold text-[#111]">{fmtMoney(inv.total - (inv.paid_amount || 0))}</span>
                                        </div>
                                        <div className="text-[10px] text-[#9CA3AF] mt-0.5">{inv.contact_name} · {inv.due_date || 'Vade yok'}</div>
                                    </button>
                                ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── AI Suggestions Panel ───
function SuggestionsPanel({ suggestions, onApprove, onReject, selectedIds, onToggleSelect, processingId }) {
    const [groupMode, setGroupMode] = useState(() => {
        try { return localStorage.getItem('acc_sug_group_mode') === '1'; } catch { return false; }
    });
    const toggleMode = () => {
        setGroupMode(prev => {
            const next = !prev;
            try { localStorage.setItem('acc_sug_group_mode', next ? '1' : '0'); } catch {}
            return next;
        });
    };

    // useMemo MUST be called unconditionally before any early return (Rules of Hooks)
    const grouped = useMemo(() => {
        const map = new Map();
        for (const sug of suggestions) {
            const key = (sug.transaction_snapshot?.counterparty_name || '(Bilinmeyen)').trim() || '(Bilinmeyen)';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(sug);
        }
        return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
    }, [suggestions]);

    if (suggestions.length === 0) {
        return <div className="bg-white rounded-xl border border-[#E5E7EB] p-12 text-center text-sm text-[#9CA3AF]" data-testid="acc-suggestions-empty">
            Oneri bulunmuyor. "Kural ile Eslestir" calistirin, sonra "AI ile Coz" ile zenginlestirin.
        </div>;
    }

    return (
        <div className="space-y-2" data-testid="acc-suggestions-list">
            <div className="flex items-center justify-between gap-2 px-1">
                <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">
                    {groupMode ? `${grouped.length} firma · ${suggestions.length} oneri` : `${suggestions.length} oneri`}
                </span>
                <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="acc-sug-groupmode-toggle">
                    <input type="checkbox" checked={groupMode} onChange={toggleMode}
                        className="w-3.5 h-3.5 accent-[#8B5CF6]"
                        data-testid="acc-sug-groupmode-cb" />
                    <span className={`text-[11px] font-semibold ${groupMode ? 'text-[#8B5CF6]' : 'text-[#6B7280]'}`}>
                        Firma Bazli Grupla{groupMode ? ' (Acik)' : ''}
                    </span>
                </label>
            </div>
            {groupMode ? (
                grouped.map(([name, sugs]) => (
                    <SuggestionGroup key={name} name={name} sugs={sugs}
                        onApprove={onApprove} onReject={onReject}
                        selectedIds={selectedIds} onToggleSelect={onToggleSelect}
                        processingId={processingId} />
                ))
            ) : (
                <div className="space-y-3">
                    {suggestions.map(sug => (
                        <SuggestionCard key={sug.id} sug={sug} onApprove={onApprove} onReject={onReject}
                            selected={selectedIds?.has(sug.id)} onToggleSelect={onToggleSelect}
                            processing={processingId === sug.id} />
                    ))}
                </div>
            )}
        </div>
    );
}

function SuggestionGroup({ name, sugs, onApprove, onReject, selectedIds, onToggleSelect, processingId }) {
    const [open, setOpen] = useState(sugs.length <= 5);   // <=5 oneri otomatik acik
    return (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid={`acc-sug-group-${name}`}>
            <button onClick={() => setOpen(o => !o)}
                className="w-full px-4 py-2.5 bg-[#FAFAFA] hover:bg-[#F3F4F6] flex items-center justify-between gap-3 text-left"
                data-testid={`acc-sug-group-toggle-${name}`}>
                <div className="flex items-center gap-2 min-w-0">
                    <ChevronRight size={14} className={`text-[#6B7280] transition-transform shrink-0 ${open ? 'rotate-90' : ''}`} />
                    <span className="text-sm font-semibold text-[#111] truncate">{name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] font-bold shrink-0">{sugs.length} oneri</span>
                </div>
            </button>
            {open && (
                <div className="p-3 space-y-2 border-t border-[#F3F4F6]">
                    {sugs.map(sug => (
                        <SuggestionCard key={sug.id} sug={sug} onApprove={onApprove} onReject={onReject}
                            selected={selectedIds?.has(sug.id)} onToggleSelect={onToggleSelect}
                            processing={processingId === sug.id} />
                    ))}
                </div>
            )}
        </div>
    );
}

function SuggestionCard({ sug, onApprove, onReject, selected, onToggleSelect, processing }) {
    const [showAlt, setShowAlt] = useState(false);
    const tx = sug.transaction_snapshot;
    const aiPick = sug.candidates.find(c => c.invoice_id === sug.ai_suggested_invoice_id) || sug.candidates[0];
    const others = (sug.candidates || []).filter(c => c.invoice_id !== aiPick?.invoice_id);
    const conf = sug.ai_confidence;
    const confColor = conf >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : conf >= 60 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-100 text-gray-600 border-gray-200';
    const multi = sug.multi_match;
    const cnAdj = sug.cn_adjusted_match;
    const ambiguous = sug.ambiguous;

    const [multiBusy, setMultiBusy] = useState(false);
    const onApproveMulti = async () => {
        if (multiBusy || processing) return;
        setMultiBusy(true);
        try {
            await axios.post(`${API}/api/accounting/reconciliation/suggestions/${sug.id}/approve`,
                { multi: true }, { headers: headers() });
            toast.success(`${multi.count} fatura birden onaylandi`);
            onApprove(sug, null, true);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
            setMultiBusy(false);
        }
    };

    const [cnAdjBusy, setCnAdjBusy] = useState(false);
    const onApproveCnAdj = async () => {
        if (cnAdjBusy || processing) return;
        setCnAdjBusy(true);
        try {
            await axios.post(`${API}/api/accounting/reconciliation/suggestions/${sug.id}/approve`,
                { cn_adjusted: true }, { headers: headers() });
            toast.success(`Iade'li odeme onaylandi · ${cnAdj.credit_notes.length} iade ofset uygulandi`);
            onApprove(sug, null, true);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
            setCnAdjBusy(false);
        }
    };

    return (
        <div className={`bg-white rounded-xl border p-4 space-y-3 ${ambiguous ? 'border-amber-300 ring-1 ring-amber-200' : selected ? 'border-[#007AFF] ring-1 ring-[#007AFF]/30' : 'border-[#E5E7EB]'}`} data-testid={`acc-sug-${sug.id}`}>
            {/* Selection checkbox + Ambiguous warning */}
            <div className="flex items-start gap-2">
                {onToggleSelect && (
                    <input type="checkbox"
                        checked={!!selected}
                        onChange={() => onToggleSelect(sug.id)}
                        className="mt-1 w-4 h-4 rounded border-[#D1D5DB] cursor-pointer shrink-0"
                        data-testid={`acc-sug-select-${sug.id}`} />
                )}
                <div className="flex-1 space-y-3">
                    {ambiguous && (
                        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`acc-sug-ambig-${sug.id}`}>
                            <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-[11px] text-amber-800 leading-relaxed">
                                <strong>DIKKAT:</strong> Ayni firmadan ayni tutarli birden fazla fatura bulundu. Lutfen dogru olani secin (genellikle vade tarihine gore en eski).
                            </p>
                        </div>
                    )}

            {/* Transaction header */}
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[#111]">{tx.counterparty_name || 'Bilinmeyen'}</span>
                        <span className="text-[11px] text-[#9CA3AF]">{tx.date}</span>
                    </div>
                    {(tx.communication || tx.reference) && (
                        <div className="text-[11px] text-[#374151] mt-1 leading-relaxed bg-[#F9FAFB] px-2 py-1 rounded break-words whitespace-pre-wrap">
                            <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mr-1">Aciklama:</span>
                            {tx.communication || '—'}
                            {tx.reference && tx.reference !== 'NOTPROVIDED' && <span className="text-[10px] text-[#9CA3AF] ml-2">(ref: {tx.reference})</span>}
                        </div>
                    )}
                    {tx.counterparty_iban && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{tx.counterparty_iban}</p>}
                </div>
                <span className={`text-base font-bold ${tx.amount >= 0 ? 'text-emerald-600' : 'text-red-500'} shrink-0`}>{tx.amount >= 0 ? '+' : ''}{fmtMoney(tx.amount)} EUR</span>
            </div>

            {/* MULTI-INVOICE MATCH: ozel kart */}
            {multi && (
                <div className="border-2 border-[#8B5CF6]/30 rounded-lg p-3 bg-gradient-to-br from-purple-50 to-pink-50">
                    <div className="flex items-center gap-1.5 mb-2">
                        <Layers size={12} className="text-[#8B5CF6]" />
                        <span className="text-[10px] font-bold text-[#8B5CF6] uppercase tracking-wider">Coklu Fatura Onerisi</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white text-[#6B7280]">{multi.count} fatura</span>
                    </div>
                    <p className="text-[12px] text-[#111] mb-2">
                        Bu odeme <strong>{fmtMoney(multi.total_amount)} EUR</strong>, <strong>{multi.contact_name}</strong> firmasinin <strong>{multi.count} faturasinin</strong> toplami:
                    </p>
                    <ul className="space-y-1 mb-2">
                        {multi.invoice_numbers.map((num, i) => (
                            <li key={i} className="flex items-center justify-between text-[11px] py-1 border-b border-purple-100 last:border-0">
                                <span className="font-medium text-[#111]">{num}</span>
                                <span className="text-[#6B7280]">{fmtMoney(multi.individual_amounts[i])} EUR</span>
                            </li>
                        ))}
                    </ul>
                    <button onClick={onApproveMulti} disabled={multiBusy || processing}
                        className="w-full h-9 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-wait"
                        data-testid={`acc-sug-approve-multi-${sug.id}`}>
                        {multiBusy ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                        {multiBusy ? 'Onaylaniyor...' : `Tum ${multi.count} Faturayi Onayla`}
                    </button>
                </div>
            )}

            {/* CN-ADJUSTED MATCH: Iade dusulmus odeme */}
            {cnAdj && (
                <div className="border-2 border-rose-300 rounded-lg p-3 bg-gradient-to-br from-rose-50 to-amber-50" data-testid={`acc-sug-cn-adj-${sug.id}`}>
                    <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                        <Link2 size={12} className="text-rose-600" />
                        <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wider">Iade Dusulmus Odeme Onerisi</span>
                        {cnAdj.exact && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">TAM ESLESME</span>}
                        {!cnAdj.exact && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">~%0.5 tolerans</span>}
                    </div>
                    <p className="text-[12px] text-[#111] mb-2 leading-relaxed">
                        Bu <strong>{fmtMoney(tx.amount)} EUR</strong> odeme,
                        <strong className="text-[#111]"> #{cnAdj.invoice_number}</strong> faturasinin
                        <strong className="text-rose-600"> bagli iade</strong> ile netlestirilmis tutari gibi gorunuyor:
                    </p>
                    <div className="bg-white rounded-md p-2 space-y-1 text-[11px] mb-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[#6B7280]">Fatura tutari (kalan):</span>
                            <strong className="text-[#111]">{fmtMoney(cnAdj.gross_remaining)} EUR</strong>
                        </div>
                        {cnAdj.credit_notes.map(c => (
                            <div key={c.id} className="flex items-center justify-between">
                                <span className="text-rose-600">– Iade #{c.invoice_number}:</span>
                                <strong className="text-rose-600">-{fmtMoney(c.total)} EUR</strong>
                            </div>
                        ))}
                        <div className="flex items-center justify-between pt-1 border-t border-[#F3F4F6]">
                            <span className="text-[#111] font-semibold">Net (beklenen):</span>
                            <strong className="text-emerald-600 text-[12px]">{fmtMoney(cnAdj.net_amount)} EUR</strong>
                        </div>
                    </div>
                    <p className="text-[10px] text-[#6B7280] italic mb-2">{cnAdj.counterparty_reason}</p>
                    <button onClick={onApproveCnAdj} disabled={cnAdjBusy || processing}
                        className="w-full h-9 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-wait"
                        data-testid={`acc-sug-approve-cn-adj-${sug.id}`}>
                        {cnAdjBusy ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                        {cnAdjBusy ? 'Onaylaniyor...' : `Fatura + ${cnAdj.credit_notes.length} Iade Birlikte Onayla`}
                    </button>
                </div>
            )}

            {/* SINGLE-INVOICE AI PICK */}
            {aiPick && (
                <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFAFA]">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <Sparkles size={12} className="text-[#8B5CF6]" />
                            <span className="text-[10px] font-bold text-[#8B5CF6] uppercase tracking-wider">{sug.ai_suggested_invoice_id ? 'AI Onerisi' : 'Kural Onerisi'}</span>
                            {conf != null && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${confColor}`}>%{conf} guven</span>}
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#6B7280]">Skor: {aiPick.rule_score}</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <div>
                            <span className="text-sm font-semibold text-[#111]">{aiPick.invoice_number}</span>
                            <span className={`ml-2 text-[10px] ${aiPick.type === 'sales' ? 'text-emerald-600' : 'text-blue-600'}`}>{aiPick.type === 'sales' ? 'Satis' : 'Alis'}</span>
                            <span className="text-[11px] text-[#6B7280] ml-2">{aiPick.contact_name}</span>
                        </div>
                        <span className="text-sm font-bold text-[#111]">{fmtMoney(aiPick.remaining)} EUR</span>
                    </div>
                    {aiPick.due_date && <p className="text-[10px] text-[#9CA3AF] mt-1">Vade: {aiPick.due_date}</p>}
                    {sug.ai_reason && <p className="text-[11px] text-[#6B7280] italic mt-1.5 leading-relaxed">{sug.ai_reason}</p>}
                </div>
            )}

            {/* Action buttons (single-invoice approval) */}
            {aiPick && (
                <div className="flex gap-2">
                    <button onClick={() => onApprove(sug, aiPick.invoice_id)} disabled={processing}
                        className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-wait"
                        data-testid={`acc-sug-approve-${sug.id}`}>
                        {processing ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                        {processing ? 'Onaylaniyor...' : (multi ? 'Tek Fatura Onayla' : 'Onayla')}
                    </button>
                    {others.length > 0 && (
                        <button onClick={() => setShowAlt(!showAlt)} disabled={processing}
                            className="h-9 px-4 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#6B7280] text-sm font-medium rounded-xl disabled:opacity-50"
                            data-testid={`acc-sug-alt-${sug.id}`}>
                            Degistir ({others.length})
                        </button>
                    )}
                    <button onClick={() => onReject(sug)} disabled={processing}
                        className="h-9 px-4 bg-white hover:bg-red-50 border border-[#E5E7EB] text-red-500 text-sm font-medium rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-50"
                        data-testid={`acc-sug-reject-${sug.id}`}>
                        <X size={14} />Reddet
                    </button>
                </div>
            )}
            {!aiPick && multi && (
                <button onClick={() => onReject(sug)} disabled={processing}
                    className="w-full h-9 px-4 bg-white hover:bg-red-50 border border-[#E5E7EB] text-red-500 text-sm font-medium rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-50"
                    data-testid={`acc-sug-reject-${sug.id}`}>
                    <X size={14} />Reddet
                </button>
            )}

            {/* Other candidates */}
            {showAlt && others.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-[#F3F4F6]">
                    <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold">Diger Adaylar</p>
                    {others.map(c => (
                        <div key={c.invoice_id} className="flex items-center gap-2 px-3 py-2 bg-[#FAFAFA] rounded-lg">
                            <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-[#111]">{c.invoice_number}</span>
                                <span className="text-[10px] text-[#9CA3AF] ml-2">{c.contact_name} · skor: {c.rule_score}{c.due_date ? ' · vade: ' + c.due_date : ''}</span>
                            </div>
                            <span className="text-xs font-bold text-[#111]">{fmtMoney(c.remaining)}</span>
                            <button onClick={() => onApprove(sug, c.invoice_id)} className="text-[10px] px-2 py-1 bg-[#111827] text-white rounded">Bunu Sec</button>
                        </div>
                    ))}
                </div>
            )}
                </div>
            </div>
        </div>
    );
}

// ─── Shared ───
function KpiCard({ icon: Icon, label, value, color, bg, sub }) {
    return (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-xl ${bg} flex items-center justify-center`}><Icon size={16} className={color} /></div>
                <span className="text-xs text-[#86868B]">{label}</span>
            </div>
            <p className={`text-lg font-bold ${color}`}>{value}</p>
            {sub && <p className="text-[11px] text-[#9CA3AF]">{sub}</p>}
        </div>
    );
}

function MiniKpi({ label, value }) {
    return (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-3">
            <p className="text-[11px] text-[#86868B] mb-0.5">{label}</p>
            <p className="text-lg font-bold text-[#1D1D1F]">{value}</p>
        </div>
    );
}
